import * as THREE from 'three';
import { spheresCollide } from './collision';
import { SHIP_BASE_Y, SHIP_Y_MAX, SHIP_Y_MIN } from './corridor';
import {
  createPlayerMissile,
  secondaryWeaponAmmoCost,
  usesFlares,
  usesTwinMinigun,
} from './createPlayerMissile';
import {
  applyObstacleDamageVisual,
  createObstacleCube,
  OBSTACLE_MAX_DAMAGE,
} from './createObstacleCube';
import type { ShipId } from './catalog';
import { createEnemyShip } from './createEnemyShip';
import { createChaff } from './createChaff';
import { createEnemyMissile } from './createEnemyMissile';
import { createFlare } from './createFlare';
import { createMinigunBolt } from './createMinigunBolt';
import { createPlayerShip } from './createPlayerShip';
import { createRailBolt } from './createRailBolt';
import { createSpaceship } from './createSpaceship';
import type { ArcadeGameCallbacks, ArcadeSceneContext, GamePhase, WeaponId } from './types';
import {
  clamp,
  damp,
  disposeObject3D,
  SHIP_BOUND_X,
  SHIP_BOUND_X_MIN_SCALE,
  SHIP_SPEED,
  STRAFE_SPEED,
  VERT_SPEED,
  TILT_DEADZONE,
  SPAWN_AHEAD,
  DESPAWN_BEHIND,
  SPAWN_INTERVAL_START,
  SPAWN_INTERVAL_MIN,
  OBSTACLE_BASE_SPEED,
  GRID_SCROLL_BOOST,
  WORLD_REBASE_Z,
  MISSILE_MAX,
  RAILGUN_MAX,
  CHAFF_MAX,
  OBSTACLE_LIMIT_MIN,
  OBSTACLE_LIMIT_MAX,
  OBSTACLE_LIMIT_DEFAULT,
  MISSILE_SPEED,
  MISSILE_HOMING,
  RAIL_SPEED,
  MISSILE_RANGE,
  RAIL_RANGE,
  MISSILE_FIRE_COOLDOWN,
  RAIL_FIRE_COOLDOWN,
  CHAFF_FIRE_COOLDOWN,
  CHAFF_PAIR_SPACING,
  CHAFF_VOLLEY_WAVES,
  FLARE_VOLLEY_WAVES,
  FLARE_FIRE_COOLDOWN,
  CHAFF_VOLLEY_INTERVAL,
  CHAFF_MISSILE_CATCH_RADIUS,
  MISSILE_SCALE,
  RAIL_SCALE,
  CHAFF_SCALE,
  CHAFF_SPEED,
  CHAFF_DROP_TIME,
  CHAFF_DROP_SPEED,
  CHAFF_DROP_FORWARD,
  CHAFF_SPAWN_DROP,
  CHAFF_FUSE,
  CHAFF_BURST_RADIUS,
  CHAFF_SPARK_MIN,
  CHAFF_SPARK_MAX,
  CHAFF_SPARK_LIFE,
  CHAFF_SPARK_PALETTE,
  CHAFF_HAZE_PALETTE,
  FLARE_LIFE,
  FLARE_FALL_SPEED,
  FLARE_BACK_SPEED,
  FLARE_DIVERGE_SPEED,
  FLARE_SCALE,
  FLARE_TRAIL_INTERVAL,
  DEBRIS_MIN,
  DEBRIS_MAX,
  DEBRIS_LIFE,
  OBSTACLE_DEBRIS_PALETTE,
  ENEMY_DEBRIS_PALETTE,
  ENEMY_MAX_HP,
  ENEMY_AHEAD,
  ENEMY_SPAWN_MAX,
  ENEMY_HORIZON_SPAWN,
  ENEMY_APPROACH_TIME,
  ENEMY_HORIZON_APPROACH_TIME,
  ENEMY_SCALE,
  ENEMY_HORIZON_SCALE_MIN,
  ENEMY_BURST_COUNT,
  ENEMY_BURST_GAP,
  ENEMY_SHOOT_INTERVAL_MIN,
  ENEMY_SHOOT_INTERVAL_MAX,
  ENEMY_LOCK_HOLD,
  SIDEWINDER_LOCK_HOLD,
  ENEMY_BOLT_SPEED,
  ENEMY_MISSILE_SPAWN_CHANCE,
  ENEMY_MISSILE_COOLDOWN,
  ENEMY_MISSILE_LOCK_HOLD,
  ENEMY_MISSILE_SPEED,
  ENEMY_MISSILE_HOMING,
  ENEMY_MISSILE_RANGE,
  ENEMY_MISSILE_SCALE,
  ENEMY_LATERAL_FOLLOW,
  ENEMY_LATERAL_DAMP,
  ENEMY_Z_DAMP,
  ENEMY_ROAM_MIN,
  ENEMY_ROAM_MAX,
  TEAMMATE_AHEAD,
  TEAMMATE_SPAWN_MAX,
  TEAMMATE_MAX_HP,
  TEAMMATE_APPROACH_TIME,
  TEAMMATE_SCALE,
  TEAMMATE_SHOOT_INTERVAL_MIN,
  TEAMMATE_SHOOT_INTERVAL_MAX,
  TEAMMATE_BURST_COUNT,
  TEAMMATE_BURST_GAP,
  TEAMMATE_BOLT_SPEED,
  TEAMMATE_ROAM_MIN,
  TEAMMATE_ROAM_MAX,
  TEAMMATE_LATERAL_DAMP,
  TEAMMATE_Z_DAMP,
  TEAMMATE_DEBRIS_PALETTE,
  ROAM_STEP_MAX,
  enemyFormationSlot,
  teammateFormationSlot,
  projectLockScreen as projectLockScreenPoint,
  pickBestLockEnemy as pickBestLockEnemyAmong,
  resolveLiveLockTarget as resolveLiveLockTargetAmong,
  createRegularDirectorState,
  regularObstacleLimit,
  rollRegularBlockLoot,
  pickRegularWaveSize,
  REGULAR_LOOT_START,
  REGULAR_WAVE_START,
  REGULAR_WAVE_CLEAR_DELAY,
  REGULAR_INTRO_ALLY_END,
  type DebrisPiece,
  type EnemyState,
  type TeammateState,
  type RegularDirectorState,
} from './engine';

/** Public tunables re-exported for UI / `game/index` consumers. */
export {
  MISSILE_MAX,
  RAILGUN_MAX,
  CHAFF_MAX,
  OBSTACLE_LIMIT_MIN,
  OBSTACLE_LIMIT_MAX,
  OBSTACLE_LIMIT_DEFAULT,
  ENEMY_SPAWN_MAX,
  TEAMMATE_SPAWN_MAX,
} from './engine';

/**
 * Endless corridor arcade: spaceship + rotating cube obstacles.
 * Reuses the desktop scene grids for travel feel; chase cam looks down -Z.
 */
export class ArcadeGame {
  private phase: GamePhase = 'idle';
  private ship: THREE.Group | null = null;
  private obstacles: THREE.Mesh[] = [];
  private missiles: THREE.Group[] = [];
  private rails: THREE.Group[] = [];
  private enemyRails: THREE.Group[] = [];
  /** Hostile lock-on missiles (home on player or chaff decoy). */
  private enemyMissiles: THREE.Group[] = [];
  /** Wingman rail bolts (do not hurt the player). */
  private allyRails: THREE.Group[] = [];
  /** Defensive chaff pods (fuse → burst; no damage to ships/blocks). */
  private chaffPods: THREE.Group[] = [];
  /** Pair-drops remaining in the active chaff/flare volley (0 = idle). */
  private chaffVolleyWavesLeft = 0;
  /** Countdown to next pair-drop within the volley. */
  private chaffVolleyTimer = 0;
  /** Active volley is F-22 flares (else classic chaff). */
  private chaffVolleyIsFlares = false;
  private debris: DebrisPiece[] = [];
  private enemies: EnemyState[] = [];
  /**
   * Sticky lock pin for one foe. Set once when soft-lock starts; never hops to
   * another living enemy until the lock is fully dropped (release / kill / weapon).
   */
  private lockTarget: EnemyState | null = null;
  private teammates: TeammateState[] = [];
  /** Sandbox god-mode: ignore bolts + block crashes (no game over). */
  private immortal = false;
  /**
   * When true (default), missiles aim at the preferred obstacle on fire.
   * When false, block auto-aim is off — shots go straight (enemy hard-lock still aims).
   * HUD block brackets still show in silver.
   */
  private blockAutoAim = true;
  /** In-run pause (settings / countdown). Freezes sim; phase stays `playing`. */
  private paused = false;
  /** Soft lock progress 0–1 while holding X / long-press. */
  private enemyLockProgress = 0;
  /** Hard lock after 3s hold — missiles always aim here. */
  private enemyHardLocked = false;
  private lockHold = false;
  /**
   * After dropping a lock (toggle-off or kill while held), ignore further hold
   * until the key/touch is released so we never auto-acquire the next foe.
   */
  private lockReleaseRequired = false;
  private ammo = MISSILE_MAX;
  private railAmmo = RAILGUN_MAX;
  private chaffAmmo = CHAFF_MAX;
  private weapon: WeaponId = 'missile';
  private fireCooldown = 0;
  /** Desktop: Space held — auto-fires railgun only (missiles stay single-shot). */
  private fireHeld = false;
  /** Sandbox: infinite ammo (no decrements / no dry-fire). */
  private sandbox = false;
  /** When false, no new obstacle cubes are spawned (sandbox control). */
  private obstaclesEnabled = true;
  /** Sandbox: max simultaneous obstacle cubes (regular mode ignores and uses DEFAULT). */
  private obstacleLimit = OBSTACLE_LIMIT_DEFAULT;
  /** Playable craft (sandbox garage); regular mode always X-Wing. */
  private shipId: ShipId = 'xwing';
  private readonly keys = new Set<string>();
  /** Device tilt as analog stick: x = left/right, y = up/down, each in [-1, 1]. */
  private readonly tilt = new THREE.Vector2(0, 0);
  /** Beta (pitch) sample captured at start — tilt is relative to hold pose. */
  private tiltNeutralBeta: number | null = null;
  private spawnTimer = 0;
  private spawnInterval = SPAWN_INTERVAL_START;
  /** Seconds of continuous flight (score base). */
  private elapsed = 0;
  /** Regular-only progression director (null in sandbox / between runs). */
  private regular: RegularDirectorState | null = null;
  /** Final score frozen at crash (ms). */
  private lastScoreMs = 0;
  private readonly idleCameraPos = new THREE.Vector3();
  private readonly lookTarget = new THREE.Vector3();

  constructor(
    private readonly ctx: ArcadeSceneContext,
    private readonly callbacks: ArcadeGameCallbacks,
  ) {}

  getPhase(): GamePhase {
    return this.phase;
  }

  isPlaying(): boolean {
    return this.phase === 'playing';
  }

  isGameOver(): boolean {
    return this.phase === 'gameover';
  }

  /** Survival time in whole milliseconds (live while playing, frozen after crash). */
  getScoreMs(): number {
    if (this.phase === 'playing') {
      return Math.floor(this.elapsed * 1000);
    }
    return this.lastScoreMs;
  }

  getAmmo(): number {
    return this.ammo;
  }

  getMaxAmmo(): number {
    return MISSILE_MAX;
  }

  getRailAmmo(): number {
    return this.railAmmo;
  }

  getMaxRailAmmo(): number {
    return RAILGUN_MAX;
  }

  getChaffAmmo(): number {
    return this.chaffAmmo;
  }

  getMaxChaffAmmo(): number {
    return CHAFF_MAX;
  }

  getWeapon(): WeaponId {
    return this.weapon;
  }

  isSandbox(): boolean {
    return this.sandbox;
  }

  /** Enable infinite ammo (sandbox) or normal limited loadouts. */
  setSandboxMode(enabled: boolean) {
    this.sandbox = enabled;
    if (enabled) {
      this.ammo = MISSILE_MAX;
      this.railAmmo = RAILGUN_MAX;
      this.chaffAmmo = CHAFF_MAX;
    } else if (this.immortal) {
      this.immortal = false;
      this.callbacks.onImmortalChange?.(false);
    }
    this.callbacks.onAmmoChange?.(this.ammo, MISSILE_MAX);
    this.callbacks.onRailgunAmmoChange?.(this.railAmmo, RAILGUN_MAX);
    this.callbacks.onChaffAmmoChange?.(this.chaffAmmo, CHAFF_MAX);
  }

  /** Sandbox: pause/resume endless block generation. */
  setObstaclesEnabled(enabled: boolean) {
    this.obstaclesEnabled = enabled;
  }

  areObstaclesEnabled(): boolean {
    return this.obstaclesEnabled;
  }

  /** Sandbox: cap how many obstacle blocks may exist at once. */
  setObstacleLimit(limit: number) {
    this.obstacleLimit = clamp(Math.round(limit), OBSTACLE_LIMIT_MIN, OBSTACLE_LIMIT_MAX);
    // Lowering the slider removes excess immediately so the change is visible
    this.enforceObstacleLimit();
  }

  getObstacleLimit(): number {
    return this.obstacleLimit;
  }

  getObstacleCount(): number {
    return this.obstacles.length;
  }

  /** Sandbox: choose playable craft (applied on next start). */
  setShipId(id: ShipId) {
    this.shipId = id;
  }

  getShipId(): ShipId {
    return this.shipId;
  }

  /** Active obstacle cap: sandbox slider, or Regular director timeline. */
  private getEffectiveObstacleLimit(): number {
    if (this.sandbox) {
      return this.obstacleLimit;
    }
    return regularObstacleLimit(this.elapsed);
  }

  /** Drop farthest-ahead cubes until count ≤ limit (sandbox slider or Regular cap). */
  private enforceObstacleLimit() {
    this.trimObstaclesTo(this.getEffectiveObstacleLimit());
  }

  private trimObstaclesTo(limit: number) {
    while (this.obstacles.length > limit) {
      // Prefer removing the one furthest ahead of the ship (most negative Z)
      let worst = 0;
      let worstZ = Infinity;
      for (let i = 0; i < this.obstacles.length; i++) {
        const z = this.obstacles[i].position.z;
        if (z < worstZ) {
          worstZ = z;
          worst = i;
        }
      }
      const cube = this.obstacles[worst];
      this.ctx.scene.remove(cube);
      disposeObject3D(cube);
      this.obstacles.splice(worst, 1);
    }
  }

  hasEnemy(): boolean {
    return this.enemies.length > 0;
  }

  getEnemyCount(): number {
    return this.enemies.length;
  }

  hasTeammate(): boolean {
    return this.teammates.length > 0;
  }

  getTeammateCount(): number {
    return this.teammates.length;
  }

  /** Sandbox god-mode: ignore enemy bolts and obstacle crashes. */
  setImmortal(enabled: boolean) {
    if (!this.sandbox && enabled) {
      // Regular scored runs stay mortal
      this.immortal = false;
      this.callbacks.onImmortalChange?.(false);
      return;
    }
    this.immortal = enabled;
    this.callbacks.onImmortalChange?.(this.immortal);
  }

  isImmortal(): boolean {
    return this.immortal;
  }

  /** Toggle missile auto-aim at obstacle blocks (HUD reticle stays; silver when off). */
  setBlockAutoAim(enabled: boolean) {
    this.blockAutoAim = enabled;
  }

  isBlockAutoAimEnabled(): boolean {
    return this.blockAutoAim;
  }

  /** Freeze gameplay (settings open, pause prompt, countdown). Score clock stops. */
  setPaused(paused: boolean) {
    this.paused = paused;
    if (paused) {
      this.fireHeld = false;
      this.lockHold = false;
    }
  }

  isPaused(): boolean {
    return this.paused;
  }

  /**
   * Hold X (desktop) or long-press (mobile) to acquire red lock on enemy.
   * A fresh press while already hard-locked drops the lock (hold-repeat is ignored).
   * After a drop/kill, the key must be released before a new acquire can start.
   */
  setLockHold(held: boolean) {
    if (held) {
      // Still waiting for a physical release after a forced drop — do not re-arm
      if (this.lockReleaseRequired) {
        return;
      }
      // Only a new press (not already holding) while hard-locked clears the lock
      if (this.enemyHardLocked && !this.lockHold) {
        this.dropEnemyLock({ requireRelease: true });
        return;
      }
      this.lockHold = true;
      return;
    }
    // Physical release: clear the re-arm gate so the next press can acquire
    this.lockHold = false;
    this.lockReleaseRequired = false;
  }

  isLockHolding(): boolean {
    return this.lockHold;
  }

  /**
   * Cycle weapons. Soft-lock acquire gesture still blocks accidental flips;
   * hard lock can be cleared by switching to railgun via setWeapon.
   */
  cycleWeaponSafe() {
    if (this.lockHold && !this.enemyHardLocked) {
      return;
    }
    this.cycleWeapon();
  }

  /**
   * Screen-space missile lock reticle (CSS px, top-left origin).
   * Block brackets stay visible even while the enemy is hard-locked.
   * `autoAim` is false when block auto-aim is disabled (HUD draws silver).
   */
  getMissileLockScreen(
    width: number,
    height: number,
  ): { x: number; y: number; size: number; autoAim: boolean } | null {
    if (this.phase !== 'playing' || this.weapon !== 'missile' || !this.ship) {
      return null;
    }
    const target = this.findMissileLockTarget();
    if (!target) {
      return null;
    }
    const base = this.projectLockScreen(target.position, width, height, this.ship.position);
    if (!base) {
      return null;
    }
    return { ...base, autoAim: this.blockAutoAim };
  }

  /**
   * Enemy reticle: orange (soft) → red (hard lock after 3s hold).
   * Soft brackets stay on the sticky pin while acquiring/hard-locked; when idle they
   * track the best engageable candidate (same always-on soft HUD as single-enemy mode).
   * Hidden entirely on railgun (missile-only lock UI). Horizon approach foes ignored.
   */
  getEnemyLockScreen(
    width: number,
    height: number,
  ): { x: number; y: number; size: number; hard: boolean; progress: number } | null {
    if (this.phase !== 'playing' || this.weapon !== 'missile' || !this.ship) {
      return null;
    }

    // Prefer sticky pin (hold / hard lock). Idle soft preview tracks best living foe.
    let target = this.getLockTargetEnemy();
    if (!target && !this.enemyHardLocked) {
      // Free-roam soft brackets (same always-on feel as single-enemy mode)
      target = this.pickBestLockEnemy({ preview: true });
    }
    if (!target) {
      return null;
    }

    const base = this.projectLockScreen(target.mesh.position, width, height, this.ship.position);
    if (!base) {
      return null;
    }
    return {
      ...base,
      hard: this.enemyHardLocked,
      progress: this.enemyHardLocked ? 1 : this.enemyLockProgress,
    };
  }

  private projectLockScreen(
    worldPos: THREE.Vector3,
    width: number,
    height: number,
    from: THREE.Vector3,
  ): { x: number; y: number; size: number } | null {
    return projectLockScreenPoint(this.ctx.camera, worldPos, width, height, from);
  }

  /**
   * Sandbox: spawn `count` foes at once (1–4). No-op if any enemy is already alive.
   * Count ≥ 3 → horizon approach for the whole wave; 1–2 → classic behind entry.
   */
  spawnEnemy(count = 1): boolean {
    if (!this.sandbox) {
      return false;
    }
    return this.spawnEnemyFormation(count, { forceRailOnly: false });
  }

  /**
   * Shared enemy wave spawn (sandbox tools + Regular director).
   * Requires no living enemies. `forceRailOnly` disables missile-capable roll (intro foe).
   */
  private spawnEnemyFormation(count: number, opts: { forceRailOnly: boolean }): boolean {
    if (this.phase !== 'playing' || !this.ship || this.enemies.length > 0) {
      return false;
    }
    const n = clamp(Math.round(count), 1, ENEMY_SPAWN_MAX);
    const ship = this.ship;
    const boundX = this.getBoundX();
    const horizonWave = n >= 3;

    for (let i = 0; i < n; i++) {
      const slot = enemyFormationSlot(i, n, boundX);
      const side = slot.x >= 0 ? 1 : -1;
      const mesh = createEnemyShip();
      mesh.name = `arcade-enemy-${i}`;
      mesh.userData.radius = 3.6;
      mesh.userData.baseScale = ENEMY_SCALE;

      if (horizonWave) {
        // Far ahead at the horizon (like obstacle cubes) — tiny + faded until closer
        mesh.scale.setScalar(ENEMY_HORIZON_SCALE_MIN);
        this.setEnemyOpacity(mesh, 0.18);
        mesh.position.set(
          ship.position.x + slot.x * 0.85,
          clamp(ship.position.y + slot.y * 0.6, SHIP_Y_MIN, SHIP_Y_MAX),
          ship.position.z - ENEMY_HORIZON_SPAWN - slot.engageExtra * 0.5,
        );
        // Nose toward the player half (+Z) while closing in
        mesh.rotation.y = 0;
      } else {
        // Classic: behind / off to the side — full size immediately
        mesh.scale.setScalar(ENEMY_SCALE);
        mesh.position.set(
          ship.position.x + side * (48 + Math.abs(slot.x) * 0.3),
          ship.position.y + slot.y + (Math.random() - 0.5) * 4,
          ship.position.z + 90 + i * 12,
        );
        mesh.rotation.y = Math.PI;
      }
      this.ctx.scene.add(mesh);

      this.enemies.push({
        mesh,
        phase: 'approach',
        approachT: 0,
        approachMode: horizonWave ? 'horizon' : 'behind',
        side,
        hp: ENEMY_MAX_HP,
        // Stagger first salvo so the wing doesn’t fire in lockstep
        shootCooldown: 0.55 + i * 0.45 + Math.random() * 0.35,
        burstLeft: 0,
        burstTimer: 0,
        avoidX: 0,
        avoidY: 0,
        follow: ship.position.clone(),
        roamX: clamp(ship.position.x + slot.x, -boundX * 0.9, boundX * 0.9),
        roamY: clamp(ship.position.y + slot.y, SHIP_Y_MIN, SHIP_Y_MAX),
        roamRetarget: ENEMY_ROAM_MIN + Math.random() * (ENEMY_ROAM_MAX - ENEMY_ROAM_MIN),
        slotX: slot.x,
        slotY: slot.y,
        engageExtra: slot.engageExtra,
        canMissile: opts.forceRailOnly ? false : Math.random() < ENEMY_MISSILE_SPAWN_CHANCE,
        missileCooldown: 0,
        missileLockProgress: 0,
      });
    }

    this.dropEnemyLock({ requireRelease: false });
    this.lockReleaseRequired = false;
    this.callbacks.onEnemyChange?.(true);
    return true;
  }

  /**
   * Player threat: max lock progress while any missile-capable foe is locking on.
   * Used for shared red corner LOCK warning UI.
   */
  getThreatLock(): { progress: number } | null {
    if (this.phase !== 'playing') {
      return null;
    }
    let best = 0;
    for (const enemy of this.enemies) {
      if (enemy.canMissile && enemy.missileLockProgress > best) {
        best = enemy.missileLockProgress;
      }
    }
    if (best <= 0) {
      return null;
    }
    return { progress: clamp(best, 0, 1) };
  }

  /**
   * Sandbox: spawn `count` friendlies at once (1–3). No-op if any ally is already alive.
   * Fan-out formation: lateral slots + slight Z stagger (same idea as multi-enemy).
   */
  spawnTeammate(count = 1): boolean {
    if (!this.sandbox || this.teammates.length > 0) {
      return false;
    }
    return this.addTeammates(count) > 0;
  }

  /**
   * Add up to `count` allies without exceeding TEAMMATE_SPAWN_MAX.
   * Used by sandbox (empty wing) and Regular intro/loot (may append).
   * @returns number actually spawned
   */
  private addTeammates(count: number): number {
    if (this.phase !== 'playing' || !this.ship || count <= 0) {
      return 0;
    }
    const room = TEAMMATE_SPAWN_MAX - this.teammates.length;
    const n = Math.min(Math.round(count), room);
    if (n <= 0) {
      return 0;
    }
    const ship = this.ship;
    const boundX = this.getBoundX();
    const startIndex = this.teammates.length;

    for (let i = 0; i < n; i++) {
      const index = startIndex + i;
      const slot = teammateFormationSlot(index, TEAMMATE_SPAWN_MAX, boundX);
      const side = slot.x >= 0 ? 1 : -1;
      const mesh = createSpaceship();
      mesh.name = `arcade-teammate-${index}`;
      mesh.scale.setScalar(TEAMMATE_SCALE);
      mesh.userData.radius = 2.2;
      // Behind / off to formation side, then approaches ahead of the player
      mesh.position.set(
        ship.position.x + side * (42 + Math.abs(slot.x) * 0.35) + slot.x * 0.25,
        ship.position.y + slot.y + (Math.random() - 0.5) * 4,
        ship.position.z + 80 + index * 10,
      );
      // Nose into the corridor (-Z), same as player
      mesh.rotation.y = Math.PI;
      this.ctx.scene.add(mesh);

      this.teammates.push({
        mesh,
        phase: 'approach',
        approachT: 0,
        side,
        hp: TEAMMATE_MAX_HP,
        // Stagger first salvo so the wing doesn’t fire in lockstep
        shootCooldown: 1.1 + index * 0.35 + Math.random() * 0.5,
        burstLeft: 0,
        burstTimer: 0,
        roamX: clamp(ship.position.x + slot.x, -boundX, boundX),
        roamY: clamp(ship.position.y + slot.y, SHIP_Y_MIN, SHIP_Y_MAX),
        roamRetarget: TEAMMATE_ROAM_MIN + Math.random() * (TEAMMATE_ROAM_MAX - TEAMMATE_ROAM_MIN),
        avoidX: 0,
        avoidY: 0,
        slotX: slot.x,
        slotY: slot.y,
        engageExtra: slot.engageExtra,
      });
    }

    this.callbacks.onTeammateChange?.(true);
    return n;
  }

  /**
   * Prefer the obstacle most likely to collide with the ship:
   * ahead on -Z, low lateral offset, nearest overall.
   */
  private findMissileLockTarget(): THREE.Mesh | null {
    const ship = this.ship;
    if (!ship || this.obstacles.length === 0) {
      return null;
    }

    let best: THREE.Mesh | null = null;
    let bestScore = Infinity;

    for (const cube of this.obstacles) {
      // Corridor forward is -Z: ahead means smaller z than the ship
      const ahead = ship.position.z - cube.position.z;
      if (ahead < 6 || ahead > SPAWN_AHEAD + 40) {
        continue;
      }
      const dx = cube.position.x - ship.position.x;
      const dy = cube.position.y - ship.position.y;
      const lateral = Math.hypot(dx, dy);
      // Soft gate: far off to the side is unlikely crash path
      if (lateral > this.getBoundX() * 1.35) {
        continue;
      }
      const dist = ship.position.distanceTo(cube.position);
      // Weight lateral heavily — “will crash into” > merely nearest anywhere
      const score = dist * 0.45 + ahead * 0.25 + lateral * 2.8;
      if (score < bestScore) {
        bestScore = score;
        best = cube;
      }
    }
    return best;
  }

  /** Dev/test: chip the nearest obstacle without firing (Playwright / console). */
  debugChipNearestObstacle(): boolean {
    if (!this.ship || this.obstacles.length === 0) {
      return false;
    }
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < this.obstacles.length; i++) {
      const d = this.obstacles[i].position.distanceTo(this.ship.position);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    this.damageObstacleAt(best, 1);
    return true;
  }

  /** Dev/test: count voxel children on obstacles (damaged look). */
  debugCountDamagedVoxels(): number {
    let n = 0;
    for (const cube of this.obstacles) {
      cube.traverse((c) => {
        if (c.name === 'obstacle-crack') n += 1;
      });
    }
    return n;
  }

  setKey(code: string, down: boolean) {
    if (down) {
      this.keys.add(code);
    } else {
      this.keys.delete(code);
    }
  }

  /** Select a weapon explicitly (HUD boxes). Leaving missiles drops enemy lock. */
  setWeapon(weapon: WeaponId) {
    if (this.weapon === weapon) {
      return;
    }
    this.weapon = weapon;
    if (weapon !== 'missile') {
      this.clearEnemyLock();
    }
    this.callbacks.onWeaponChange?.(weapon);
  }

  /** Cycle missile → railgun → chaff → missile (V / click / HUD). */
  cycleWeapon() {
    if (this.weapon === 'missile') {
      this.setWeapon('railgun');
    } else if (this.weapon === 'railgun') {
      this.setWeapon('chaff');
    } else {
      this.setWeapon('missile');
    }
  }

  /** Drop soft/hard enemy lock and any in-progress hold (keeps release gate as-is). */
  private clearEnemyLock() {
    this.enemyHardLocked = false;
    this.enemyLockProgress = 0;
    this.lockHold = false;
    this.lockTarget = null;
  }

  /**
   * Fully drop enemy lock. When `requireRelease` is true the player must release
   * the lock key/touch before another soft-lock can start (prevents auto-retarget).
   */
  private dropEnemyLock(opts: { requireRelease: boolean }) {
    this.clearEnemyLock();
    if (opts.requireRelease) {
      this.lockReleaseRequired = true;
    }
  }

  /** Track Space hold (desktop). Only railgun auto-fires while held. */
  setFireHeld(held: boolean) {
    this.fireHeld = held;
  }

  /**
   * Fire the active weapon if ammo and cooldown allow.
   * Desktop: Space / F (hold Space for continuous railgun). Mobile: tap playfield.
   */
  tryFire(): boolean {
    if (this.phase !== 'playing' || !this.ship || this.paused) {
      return false;
    }
    if (this.fireCooldown > 0) {
      return false;
    }

    if (this.weapon === 'missile') {
      if (!this.sandbox && this.ammo <= 0) {
        return false;
      }
      if (!this.sandbox) {
        this.ammo -= 1;
        this.callbacks.onAmmoChange?.(this.ammo, MISSILE_MAX);
      }
      this.fireCooldown = MISSILE_FIRE_COOLDOWN;
      this.spawnMissile();
      return true;
    }

    if (this.weapon === 'chaff') {
      if (!this.sandbox && this.chaffAmmo <= 0) {
        return false;
      }
      // Don’t stack a second volley while one is still dumping pairs
      if (this.chaffVolleyWavesLeft > 0) {
        return false;
      }
      if (!this.sandbox) {
        this.chaffAmmo -= 1;
        this.callbacks.onChaffAmmoChange?.(this.chaffAmmo, CHAFF_MAX);
      }
      const flares = usesFlares(this.shipId);
      this.fireCooldown = flares ? FLARE_FIRE_COOLDOWN : CHAFF_FIRE_COOLDOWN;
      this.startCountermeasureVolley(flares);
      return true;
    }

    // railgun / F-22 twin minigun (2× ammo cost, dual barrels)
    const secondaryCost = secondaryWeaponAmmoCost(this.shipId);
    if (!this.sandbox && this.railAmmo < secondaryCost) {
      return false;
    }
    if (!this.sandbox) {
      this.railAmmo -= secondaryCost;
      this.callbacks.onRailgunAmmoChange?.(this.railAmmo, RAILGUN_MAX);
    }
    this.fireCooldown = RAIL_FIRE_COOLDOWN;
    if (usesTwinMinigun(this.shipId)) {
      this.spawnTwinMinigunBolts();
    } else {
      this.spawnRailBolt();
    }
    return true;
  }

  /**
   * Map device orientation to flight input.
   * - gamma (roll): tilt phone left/right → strafe
   * - beta (pitch): tilt forward/back relative to neutral hold → climb/dive
   */
  setOrientation(beta: number | null, gamma: number | null) {
    if (beta == null || gamma == null || Number.isNaN(beta) || Number.isNaN(gamma)) {
      return;
    }
    if (this.tiltNeutralBeta === null) {
      this.tiltNeutralBeta = beta;
    }
    // ~±28° roll → full strafe
    const x = clamp(gamma / 28, -1, 1);
    // Tilt top toward player (beta ↑) = climb; top away (beta ↓) = dive
    const y = clamp((beta - this.tiltNeutralBeta) / 22, -1, 1);
    this.tilt.set(x, y);
  }

  /**
   * Horizontal bound scaled by viewport aspect so portrait mobile keeps the
   * ship (and obstacles) inside the visible corridor, not off the side edges.
   */
  getBoundX(): number {
    const aspect =
      this.ctx.camera.aspect > 0
        ? this.ctx.camera.aspect
        : typeof window !== 'undefined'
          ? window.innerWidth / Math.max(window.innerHeight, 1)
          : 1.5;
    // aspect ≥ ~1.25 (landscape) → full bound; narrow portrait → min scale
    const scale = clamp(aspect / 1.25, SHIP_BOUND_X_MIN_SCALE, 1);
    return SHIP_BOUND_X * scale;
  }

  /** Begin play — caller must ensure no desktop windows are open. */
  start() {
    if (this.phase === 'playing' || this.phase === 'gameover') {
      return;
    }

    const { scene, camera, idleCube } = this.ctx;
    this.idleCameraPos.copy(camera.position);
    this.phase = 'playing';
    this.elapsed = 0;
    this.lastScoreMs = 0;
    this.spawnTimer = 0;
    this.spawnInterval = SPAWN_INTERVAL_START;
    this.obstaclesEnabled = true;
    this.ammo = MISSILE_MAX;
    this.railAmmo = RAILGUN_MAX;
    this.chaffAmmo = CHAFF_MAX;
    this.weapon = 'missile';
    this.fireCooldown = 0;
    this.fireHeld = false;
    this.dropEnemyLock({ requireRelease: false });
    this.lockReleaseRequired = false;
    this.immortal = false;
    this.paused = false;
    this.callbacks.onImmortalChange?.(false);
    // sandbox flag is sticky across runs (set via setSandboxMode / menu)
    this.regular = this.sandbox ? null : createRegularDirectorState();
    this.keys.clear();
    this.tilt.set(0, 0);
    // Recalibrate gyro to whatever angle the player is holding the phone at
    this.tiltNeutralBeta = null;

    idleCube.visible = false;

    // Sandbox/Dev garage craft; regular scored runs always use X-Wing
    const shipId = this.sandbox ? this.shipId : 'xwing';
    const ship = createPlayerShip(shipId);
    ship.name = 'arcade-player';
    // Nose toward -Z (into the horizon)
    ship.rotation.y = Math.PI;
    ship.position.set(0, SHIP_BASE_Y, 125);
    ship.scale.setScalar(1.6);
    if (typeof ship.userData.radius !== 'number') {
      ship.userData.radius = 2.2;
    }
    scene.add(ship);
    this.ship = ship;

    // Regular starts with timeline block cap (2); sandbox keeps slider value
    if (!this.sandbox) {
      this.enforceObstacleLimit();
    }

    this.callbacks.onPhaseChange('playing');
    this.callbacks.onAmmoChange?.(this.ammo, MISSILE_MAX);
    this.callbacks.onRailgunAmmoChange?.(this.railAmmo, RAILGUN_MAX);
    this.callbacks.onChaffAmmoChange?.(this.chaffAmmo, CHAFF_MAX);
    this.callbacks.onWeaponChange?.(this.weapon);
  }

  /** Crash → clean ship, freeze score, show game-over UI (caller dismisses to idle). */
  end() {
    if (this.phase !== 'playing') {
      return;
    }
    this.lastScoreMs = Math.floor(this.elapsed * 1000);
    this.cleanupPlayObjects();
    this.phase = 'gameover';
    this.ctx.idleCube.visible = true;
    this.ctx.camera.position.copy(this.idleCameraPos);
    this.callbacks.onGameOver?.(this.lastScoreMs);
    this.callbacks.onPhaseChange('gameover');
  }

  /** Dismiss game-over panel and return to the idle desktop. */
  acknowledgeGameOver() {
    if (this.phase !== 'gameover') {
      return;
    }
    this.phase = 'idle';
    this.keys.clear();
    this.tilt.set(0, 0);
    this.callbacks.onPhaseChange('idle');
  }

  /**
   * Per-frame tick. When idle, returns false so the host can run desktop camera.
   * When playing, updates ship, obstacles, camera, grids, and collision.
   */
  update(
    dt: number,
    mouse: THREE.Vector2,
    cameraCenter: THREE.Vector3,
  ): 'idle' | 'playing' | 'gameover' {
    if (this.phase !== 'playing' || !this.ship) {
      return this.phase === 'gameover' ? 'gameover' : 'idle';
    }

    // Settings / resume countdown: hold world still (camera can still track)
    if (this.paused) {
      this.updateChaseCamera(mouse, cameraCenter);
      return 'playing';
    }

    this.elapsed += dt;
    if (this.fireCooldown > 0) {
      this.fireCooldown = Math.max(0, this.fireCooldown - dt);
    }
    // Hold-to-fire: railgun only (desktop Space held)
    if (this.fireHeld && this.weapon === 'railgun') {
      this.tryFire();
    }
    this.spawnInterval = Math.max(SPAWN_INTERVAL_MIN, SPAWN_INTERVAL_START - this.elapsed * 0.02);

    // Regular progression (caps, intro spawns, waves) before world sim
    if (!this.sandbox && this.regular) {
      this.tickRegularDirector();
    }

    this.updateShip(dt);
    this.updateEnemy(dt);
    this.updateTeammate(dt);
    this.updateEnemyLock(dt);
    this.updateMissiles(dt);
    this.updateRails(dt);
    this.updateChaffVolley(dt);
    this.updateChaff(dt);
    this.updateEnemyRails(dt);
    this.updateEnemyMissiles(dt);
    this.updateAllyRails(dt);
    this.updateDebris(dt);
    this.updateObstacles(dt);
    this.spawnTimer += dt;
    if (this.obstaclesEnabled && this.spawnTimer >= this.spawnInterval) {
      // Honor sandbox slider or Regular timeline cap
      const underCap = this.obstacles.length < this.getEffectiveObstacleLimit();
      if (underCap) {
        this.spawnTimer = 0;
        this.spawnObstacle();
      }
      // If at cap, leave the timer armed so a despawn can refill on the next frame
    }

    // Keep world coords near the origin so infinite grids + camera stay stable forever
    this.rebaseWorldIfNeeded();

    // Faster grid scroll while flying
    for (const mat of this.ctx.gridMaterials) {
      mat.uniforms.uTime.value += dt * GRID_SCROLL_BOOST;
    }

    this.updateChaseCamera(mouse, cameraCenter);

    // Wingmen can die without ending the run
    this.checkTeammateHazards();

    if (this.immortal) {
      // God-mode: ram blocks shatter; hostile fire is absorbed (removed), not left to
      // home through the ship (that looked like weird “deflection”).
      this.shatterCollidingObstacles();
      this.absorbEnemyProjectilesOnPlayer();
    } else if (
      this.checkCollisions() ||
      this.checkEnemyBoltHitsPlayer() ||
      this.checkEnemyMissileHitsPlayer()
    ) {
      this.end();
      return 'gameover';
    }

    return 'playing';
  }

  dispose() {
    this.cleanupPlayObjects();
    this.keys.clear();
    this.phase = 'idle';
  }

  /**
   * Ship travels endlessly on -Z. When |z| gets large, shift ship, obstacles,
   * and camera back toward 0 so shader grids (camera-centered) stay accurate.
   */
  private rebaseWorldIfNeeded() {
    const ship = this.ship;
    if (!ship || ship.position.z > WORLD_REBASE_Z) {
      return;
    }

    const shift = -ship.position.z; // positive: move everything toward +Z
    ship.position.z += shift;
    for (const cube of this.obstacles) {
      cube.position.z += shift;
    }
    for (const missile of this.missiles) {
      missile.position.z += shift;
    }
    for (const bolt of this.rails) {
      bolt.position.z += shift;
    }
    for (const bolt of this.enemyRails) {
      bolt.position.z += shift;
    }
    for (const missile of this.enemyMissiles) {
      missile.position.z += shift;
    }
    for (const bolt of this.allyRails) {
      bolt.position.z += shift;
    }
    for (const pod of this.chaffPods) {
      pod.position.z += shift;
    }
    for (const enemy of this.enemies) {
      enemy.mesh.position.z += shift;
      enemy.follow.z += shift;
    }
    for (const mate of this.teammates) {
      mate.mesh.position.z += shift;
    }
    for (const piece of this.debris) {
      piece.mesh.position.z += shift;
    }
    this.ctx.camera.position.z += shift;
    this.lookTarget.z += shift;
  }

  private spawnMissile() {
    const ship = this.ship;
    if (!ship) return;

    // F-22 fires AIM-9 Sidewinder; other craft keep the brick warhead missile
    const missile = createPlayerMissile(this.shipId);
    missile.position.set(ship.position.x, ship.position.y, ship.position.z - 3.8);
    // Sidewinder is already longer/slimmer; slight scale bump for corridor readability
    const scale = this.shipId === 'f22' ? MISSILE_SCALE * 1.05 : MISSILE_SCALE;
    missile.scale.setScalar(scale);

    // Hard enemy lock → aim + home on foe; else crash-path block; else straight
    const homeOnEnemy = Boolean(this.enemyHardLocked && this.getLockTargetEnemy());
    missile.userData.homingEnemy = homeOnEnemy;

    const aimPoint = this.getMissileAimPoint();
    const forward = new THREE.Vector3(0, 0, -1);
    if (aimPoint) {
      const dir = new THREE.Vector3().subVectors(aimPoint, missile.position);
      if (dir.lengthSq() > 0.0001) {
        dir.normalize();
        missile.userData.velocity = dir.multiplyScalar(MISSILE_SPEED);
        missile.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);
      } else {
        missile.userData.velocity = forward.clone().multiplyScalar(MISSILE_SPEED);
        missile.rotation.y = Math.PI;
      }
    } else {
      missile.userData.velocity = forward.clone().multiplyScalar(MISSILE_SPEED);
      missile.rotation.y = Math.PI;
    }

    this.ctx.scene.add(missile);
    this.missiles.push(missile);
  }

  /**
   * Steer a hard-locked missile toward the live enemy position (constant speed).
   * Pure pursuit with exponential turn blend — follows lateral movement without aim-bot snap.
   */
  private steerHomingMissile(proj: THREE.Group, dt: number) {
    const target = this.getLockTargetEnemy();
    if (!proj.userData.homingEnemy || !target) {
      return;
    }
    const vel = proj.userData.velocity as THREE.Vector3 | undefined;
    if (!vel) {
      return;
    }

    const toTarget = new THREE.Vector3().subVectors(target.mesh.position, proj.position);
    if (toTarget.lengthSq() < 0.0001) {
      return;
    }
    toTarget.normalize();

    // Blend current heading toward target; re-clamp to MISSILE_SPEED
    const turn = 1 - Math.exp(-MISSILE_HOMING * dt);
    const desired = toTarget.multiplyScalar(MISSILE_SPEED);
    vel.lerp(desired, turn);
    if (vel.lengthSq() > 0.0001) {
      vel.normalize().multiplyScalar(MISSILE_SPEED);
      proj.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), vel.clone().normalize());
    }
  }

  private getMissileAimPoint(): THREE.Vector3 | null {
    if (this.enemyHardLocked) {
      const target = this.getLockTargetEnemy();
      if (target) {
        return target.mesh.position.clone();
      }
    }
    // Block auto-aim off → fire straight; HUD still highlights preferred cube.
    if (!this.blockAutoAim) {
      return null;
    }
    const cube = this.findMissileLockTarget();
    return cube ? cube.position.clone() : null;
  }

  private spawnRailBolt() {
    const ship = this.ship;
    if (!ship) return;

    const bolt = createRailBolt();
    bolt.rotation.y = Math.PI;
    bolt.position.set(ship.position.x, ship.position.y, ship.position.z - 3.2);
    bolt.scale.setScalar(RAIL_SCALE);
    bolt.userData.velocity = new THREE.Vector3(0, 0, -RAIL_SPEED);
    this.ctx.scene.add(bolt);
    this.rails.push(bolt);
  }

  /**
   * F-22 twin minigun: one golden slug from each wing root (2 rounds spent in tryFire).
   */
  private spawnTwinMinigunBolts() {
    const ship = this.ship;
    if (!ship) return;

    // Wing hardpoints — lateral offset in world X (ship flies −Z)
    const lateral = 1.35;
    const ahead = 3.0;
    const drop = 0.15;
    for (const sx of [-1, 1] as const) {
      const bolt = createMinigunBolt();
      bolt.rotation.y = Math.PI;
      bolt.position.set(
        ship.position.x + sx * lateral,
        ship.position.y - drop,
        ship.position.z - ahead,
      );
      bolt.scale.setScalar(RAIL_SCALE * 0.95);
      bolt.userData.velocity = new THREE.Vector3(0, 0, -RAIL_SPEED);
      this.ctx.scene.add(bolt);
      this.rails.push(bolt);
    }
  }

  /**
   * Countermeasure volley: chaff (10×2, forward pop) or F-22 flares (15×2, fall-back trail).
   * First pair drops immediately.
   */
  private startCountermeasureVolley(flares: boolean) {
    this.chaffVolleyWavesLeft = flares ? FLARE_VOLLEY_WAVES : CHAFF_VOLLEY_WAVES;
    this.chaffVolleyTimer = 0;
    this.chaffVolleyIsFlares = flares;
    this.spawnCountermeasurePair(flares);
    this.chaffVolleyWavesLeft -= 1;
    if (this.chaffVolleyWavesLeft > 0) {
      this.chaffVolleyTimer = CHAFF_VOLLEY_INTERVAL;
    }
  }

  private updateChaffVolley(dt: number) {
    if (this.chaffVolleyWavesLeft <= 0) {
      return;
    }
    this.chaffVolleyTimer -= dt;
    if (this.chaffVolleyTimer > 0) {
      return;
    }
    this.spawnCountermeasurePair(this.chaffVolleyIsFlares);
    this.chaffVolleyWavesLeft -= 1;
    if (this.chaffVolleyWavesLeft > 0) {
      this.chaffVolleyTimer = CHAFF_VOLLEY_INTERVAL;
    }
  }

  /** Two pods / flares side-by-side under the ship. */
  private spawnCountermeasurePair(flares: boolean) {
    const half = CHAFF_PAIR_SPACING * 0.5;
    if (flares) {
      this.spawnFlarePod(-half);
      this.spawnFlarePod(half);
    } else {
      this.spawnChaffPod(-half);
      this.spawnChaffPod(half);
    }
  }

  /**
   * Single chaff pod: starts below the ship (with lateral offset), dips, then
   * flies straight and pops (no ship/block damage).
   */
  private spawnChaffPod(lateralOffset: number) {
    const ship = this.ship;
    if (!ship) return;

    const pod = createChaff();
    pod.rotation.y = Math.PI;
    // Appear under the craft, offset left/right for the parallel pair
    pod.position.set(
      ship.position.x + lateralOffset,
      ship.position.y - CHAFF_SPAWN_DROP,
      ship.position.z - 3.4,
    );
    pod.scale.setScalar(CHAFF_SCALE);
    // Drop phase: sink + soft forward, then level into full −Z cruise
    pod.userData.kind = 'chaff';
    pod.userData.phase = 'drop';
    pod.userData.dropT = CHAFF_DROP_TIME;
    pod.userData.velocity = new THREE.Vector3(0, -CHAFF_DROP_SPEED, -CHAFF_DROP_FORWARD);
    pod.userData.fuse = CHAFF_FUSE;
    this.ctx.scene.add(pod);
    this.chaffPods.push(pod);
  }

  /**
   * IR flare: slow divergent fall back behind the ship with a rocket-style trail.
   * No firework burst — burns out quietly. Still a missile decoy while alive.
   */
  private spawnFlarePod(lateralOffset: number) {
    const ship = this.ship;
    if (!ship) return;

    const side = lateralOffset >= 0 ? 1 : -1;
    const flare = createFlare();
    flare.position.set(
      ship.position.x + lateralOffset * 0.55,
      ship.position.y - 1.2,
      ship.position.z + 1.2,
    );
    flare.scale.setScalar(FLARE_SCALE);
    // Divergent: outward + back (+Z) + down (−Y), slow “falling torch”
    const diverge = FLARE_DIVERGE_SPEED * (0.75 + Math.random() * 0.45);
    const back = FLARE_BACK_SPEED * (0.85 + Math.random() * 0.3);
    const fall = FLARE_FALL_SPEED * (0.7 + Math.random() * 0.4);
    flare.userData.kind = 'flare';
    flare.userData.velocity = new THREE.Vector3(side * diverge, -fall * 0.55, back);
    flare.userData.fuse = FLARE_LIFE;
    flare.userData.trailAcc = 0;
    this.ctx.scene.add(flare);
    this.chaffPods.push(flare);
  }

  /**
   * Chaff: drop → straight → gold burst.
   * Flares: slow fall-back diverge + trail sparks, then fade (no pop).
   * Both act as decoys for enemy missiles while alive.
   */
  private updateChaff(dt: number) {
    const remaining: THREE.Group[] = [];
    for (const pod of this.chaffPods) {
      const isFlare = pod.userData.kind === 'flare';

      if (isFlare) {
        const vel = pod.userData.velocity as THREE.Vector3 | undefined;
        if (vel) {
          // Soft gravity + slight drag on horizontal
          vel.y -= 9 * dt;
          vel.x *= 1 - 0.12 * dt;
          vel.z *= 1 - 0.08 * dt;
          pod.position.addScaledVector(vel, dt);
          // Point roughly along velocity for trail read
          if (vel.lengthSq() > 0.01) {
            const dir = vel.clone().normalize();
            pod.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);
          }
        }
        // Rocket-style trail (small orange sparks, not a firecracker pop)
        let trailAcc = typeof pod.userData.trailAcc === 'number' ? pod.userData.trailAcc : 0;
        trailAcc += dt;
        while (trailAcc >= FLARE_TRAIL_INTERVAL) {
          trailAcc -= FLARE_TRAIL_INTERVAL;
          this.spawnFlareTrailSpark(pod.position);
        }
        pod.userData.trailAcc = trailAcc;
      } else {
        // Chaff: transition out of the dip into level flight
        if (pod.userData.phase === 'drop') {
          const dropT = typeof pod.userData.dropT === 'number' ? pod.userData.dropT : 0;
          const nextDrop = dropT - dt;
          if (nextDrop <= 0) {
            pod.userData.phase = 'straight';
            pod.userData.dropT = 0;
            pod.userData.velocity = new THREE.Vector3(0, 0, -CHAFF_SPEED);
          } else {
            pod.userData.dropT = nextDrop;
            const t = nextDrop / CHAFF_DROP_TIME;
            const vel = pod.userData.velocity as THREE.Vector3;
            vel.set(
              0,
              -CHAFF_DROP_SPEED * t,
              -CHAFF_DROP_FORWARD - (CHAFF_SPEED - CHAFF_DROP_FORWARD) * (1 - t),
            );
          }
        }
        const vel = pod.userData.velocity as THREE.Vector3 | undefined;
        if (vel) {
          pod.position.addScaledVector(vel, dt);
        }
        pod.position.y = clamp(pod.position.y, SHIP_Y_MIN, SHIP_Y_MAX);
      }

      const fuse = typeof pod.userData.fuse === 'number' ? pod.userData.fuse : 0;
      const nextFuse = fuse - dt;
      if (nextFuse <= 0) {
        if (isFlare) {
          this.expireFlare(pod);
        } else {
          this.detonateChaff(pod);
        }
        continue;
      }
      pod.userData.fuse = nextFuse;
      remaining.push(pod);
    }
    this.chaffPods = remaining;
  }

  /** Quiet burn-out: remove flare without fireworks (missiles only die on proximity while live). */
  private expireFlare(pod: THREE.Group) {
    this.ctx.scene.remove(pod);
    disposeObject3D(pod);
  }

  /** Soft rocket trail spark behind a burning flare. */
  private spawnFlareTrailSpark(origin: THREE.Vector3) {
    const size = 0.12 + Math.random() * 0.14;
    const colors = [0xfff59d, 0xffd54f, 0xff8f00, 0xff6f00, 0xffab40];
    const color = colors[Math.floor(Math.random() * colors.length)] ?? 0xffd54f;
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(size, size, size * 1.4),
      new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.85 }),
    );
    mesh.position.copy(origin);
    mesh.position.x += (Math.random() - 0.5) * 0.15;
    mesh.position.y += (Math.random() - 0.5) * 0.15;
    mesh.position.z += (Math.random() - 0.5) * 0.15;
    const life = 0.28 + Math.random() * 0.2;
    this.ctx.scene.add(mesh);
    this.debris.push({
      mesh,
      velocity: new THREE.Vector3(
        (Math.random() - 0.5) * 2,
        1 + Math.random() * 3,
        (Math.random() - 0.5) * 2,
      ),
      spin: new THREE.Vector3(
        (Math.random() - 0.5) * 6,
        (Math.random() - 0.5) * 6,
        (Math.random() - 0.5) * 6,
      ),
      life,
      maxLife: life,
    });
  }

  /**
   * Prefer the nearest live chaff pod as a decoy target for enemy missiles.
   * Returns null when no chaff is deployed.
   */
  private getChaffDecoy(from: THREE.Vector3): THREE.Group | null {
    if (this.chaffPods.length === 0) {
      return null;
    }
    let best: THREE.Group | null = null;
    let bestDist = Infinity;
    for (const pod of this.chaffPods) {
      const d = from.distanceToSquared(pod.position);
      if (d < bestDist) {
        bestDist = d;
        best = pod;
      }
    }
    return best;
  }

  /**
   * Sticky decoy for a hostile missile: keep the same pod until it dies, then re-pick.
   * Returns null only when no chaff is live (missile falls back to the player).
   */
  private resolveMissileDecoy(missile: THREE.Group): THREE.Group | null {
    const pinned = missile.userData.decoyPod as THREE.Group | undefined;
    if (pinned && this.chaffPods.includes(pinned)) {
      return pinned;
    }
    const next = this.getChaffDecoy(missile.position);
    missile.userData.decoyPod = next;
    return next;
  }

  /** Aim point for hostile missiles: chaff decoy if present, otherwise the player. */
  private getEnemyMissileAimPoint(missile: THREE.Group): THREE.Vector3 | null {
    const decoy = this.resolveMissileDecoy(missile);
    if (decoy) {
      return decoy.position.clone();
    }
    return this.ship ? this.ship.position.clone() : null;
  }

  private detonateChaff(pod: THREE.Group) {
    const origin = pod.position.clone();
    this.spawnChaffSparkBurst(origin);

    // Destroy enemy bolts in the burst radius (defense only)
    for (let i = this.enemyRails.length - 1; i >= 0; i--) {
      const bolt = this.enemyRails[i];
      const br = typeof bolt.userData.radius === 'number' ? bolt.userData.radius : 1;
      if (origin.distanceTo(bolt.position) <= CHAFF_BURST_RADIUS + br) {
        this.ctx.scene.remove(bolt);
        disposeObject3D(bolt);
        this.enemyRails.splice(i, 1);
      }
    }

    this.ctx.scene.remove(pod);
    disposeObject3D(pod);
  }

  /** Detonate and drop from the live pod list (safe for bolt-hit path). */
  private destroyChaffPod(pod: THREE.Group) {
    if (pod.userData.kind === 'flare') {
      this.expireFlare(pod);
    } else {
      this.detonateChaff(pod);
    }
    const idx = this.chaffPods.indexOf(pod);
    if (idx >= 0) {
      this.chaffPods.splice(idx, 1);
    }
  }

  /** Yellow / gold sparks + soft golden haze at the chaff pop point. */
  private spawnChaffSparkBurst(origin: THREE.Vector3) {
    const count =
      CHAFF_SPARK_MIN + Math.floor(Math.random() * (CHAFF_SPARK_MAX - CHAFF_SPARK_MIN + 1));
    // Visual spread roughly tracks the bolt-eat radius so the pop reads as wide as it hits
    const spread = CHAFF_BURST_RADIUS * 0.22;
    for (let i = 0; i < count; i++) {
      const isHaze = i % 4 === 0;
      const size = isHaze ? 0.7 + Math.random() * 0.55 : 0.22 + Math.random() * 0.35;
      const color = isHaze
        ? CHAFF_HAZE_PALETTE[Math.floor(Math.random() * CHAFF_HAZE_PALETTE.length)]
        : CHAFF_SPARK_PALETTE[Math.floor(Math.random() * CHAFF_SPARK_PALETTE.length)];
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(size, size * (0.6 + Math.random() * 0.5), size),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: isHaze ? 0.5 : 1 }),
      );
      mesh.position.copy(origin);
      mesh.position.x += (Math.random() * 2 - 1) * spread;
      mesh.position.y += (Math.random() * 2 - 1) * spread;
      mesh.position.z += (Math.random() * 2 - 1) * spread;
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);

      const speed = isHaze ? 10 + Math.random() * 16 : 22 + Math.random() * 36;
      const dir = new THREE.Vector3(
        (Math.random() * 2 - 1) * 1.2,
        (Math.random() * 2 - 1) * 1.2 + 0.15,
        (Math.random() * 2 - 1) * 1.1,
      ).normalize();

      const life = CHAFF_SPARK_LIFE * (isHaze ? 1.15 : 0.7 + Math.random() * 0.5);
      this.ctx.scene.add(mesh);
      this.debris.push({
        mesh,
        velocity: dir.multiplyScalar(speed),
        spin: new THREE.Vector3(
          (Math.random() * 2 - 1) * 10,
          (Math.random() * 2 - 1) * 10,
          (Math.random() * 2 - 1) * 10,
        ),
        life,
        maxLife: life,
      });
    }
  }

  private updateMissiles(dt: number) {
    this.missiles = this.advanceProjectiles(this.missiles, dt, MISSILE_RANGE, 3, ENEMY_MAX_HP);
  }

  private updateRails(dt: number) {
    this.rails = this.advanceProjectiles(this.rails, dt, RAIL_RANGE, 1, 1);
  }

  private updateEnemyRails(dt: number) {
    const ship = this.ship;
    if (!ship) return;
    const remaining: THREE.Group[] = [];
    for (const bolt of this.enemyRails) {
      const vel = bolt.userData.velocity as THREE.Vector3 | undefined;
      // Straight corridor fire only — no player/chaff homing (missiles handle that).
      if (vel) {
        bolt.position.addScaledVector(vel, dt);
      }

      let consumed = false;

      // Passive chaff collision if a straight bolt flies into a pod (not guided)
      for (let ci = this.chaffPods.length - 1; ci >= 0; ci--) {
        const pod = this.chaffPods[ci];
        if (spheresCollide(bolt, pod)) {
          this.destroyChaffPod(pod);
          consumed = true;
          break;
        }
      }
      if (consumed) {
        this.ctx.scene.remove(bolt);
        disposeObject3D(bolt);
        continue;
      }

      // Friendly fire: enemy bolts can kill other foes (not the shooter)
      const owner = bolt.userData.ownerMesh as THREE.Object3D | undefined;
      for (let ei = this.enemies.length - 1; ei >= 0; ei--) {
        const foe = this.enemies[ei];
        if (owner && foe.mesh === owner) continue;
        if (spheresCollide(bolt, foe.mesh)) {
          this.damageEnemyAt(ei, 1);
          consumed = true;
          break;
        }
      }
      if (consumed) {
        this.ctx.scene.remove(bolt);
        disposeObject3D(bolt);
        continue;
      }

      // Enemy bolts damage obstacles (1 dmg each → 2 hits to destroy, same as player rail)
      for (let i = this.obstacles.length - 1; i >= 0; i--) {
        const cube = this.obstacles[i];
        if (spheresCollide(bolt, cube)) {
          this.damageObstacleAt(i, 1);
          consumed = true;
          break;
        }
      }
      if (consumed) {
        this.ctx.scene.remove(bolt);
        disposeObject3D(bolt);
        continue;
      }

      const traveled = ship.position.distanceTo(bolt.position);
      if (traveled > RAIL_RANGE + 40) {
        this.ctx.scene.remove(bolt);
        disposeObject3D(bolt);
        continue;
      }
      remaining.push(bolt);
    }
    this.enemyRails = remaining;
  }

  private checkEnemyBoltHitsPlayer(): boolean {
    const ship = this.ship;
    if (!ship) return false;
    for (let i = this.enemyRails.length - 1; i >= 0; i--) {
      const bolt = this.enemyRails[i];
      if (spheresCollide(bolt, ship)) {
        this.ctx.scene.remove(bolt);
        disposeObject3D(bolt);
        this.enemyRails.splice(i, 1);
        return true;
      }
    }
    return false;
  }

  /**
   * Immortal: remove every enemy bolt/missile overlapping the ship this frame.
   * Avoids pass-through + continuous homing (looked like deflection).
   */
  private absorbEnemyProjectilesOnPlayer() {
    while (this.checkEnemyBoltHitsPlayer()) {
      /* absorb all overlapping bolts */
    }
    while (this.checkEnemyMissileHitsPlayer()) {
      /* absorb all overlapping missiles */
    }
  }

  /**
   * Enemy bolts and obstacle contact can destroy wingmen mid-run
   * without ending the player’s game.
   */
  private checkTeammateHazards() {
    if (this.teammates.length === 0) return;

    // Enemy fire hits allies
    for (let i = this.enemyRails.length - 1; i >= 0; i--) {
      const bolt = this.enemyRails[i];
      let hitAlly = false;
      for (let ti = this.teammates.length - 1; ti >= 0; ti--) {
        if (spheresCollide(bolt, this.teammates[ti].mesh)) {
          this.ctx.scene.remove(bolt);
          disposeObject3D(bolt);
          this.enemyRails.splice(i, 1);
          this.damageTeammateAt(ti, 1);
          hitAlly = true;
          break;
        }
      }
      if (hitAlly) continue;
    }

    // Obstacle collision destroys the wingman
    for (let ti = this.teammates.length - 1; ti >= 0; ti--) {
      const mate = this.teammates[ti];
      for (const cube of this.obstacles) {
        if (spheresCollide(mate.mesh, cube)) {
          this.destroyTeammateAt(ti);
          break;
        }
      }
    }
  }

  private updateAllyRails(dt: number) {
    const ship = this.ship;
    if (!ship) return;
    const remaining: THREE.Group[] = [];
    for (const bolt of this.allyRails) {
      const vel = bolt.userData.velocity as THREE.Vector3 | undefined;
      if (vel) {
        bolt.position.addScaledVector(vel, dt);
      }

      let hit = false;
      // Ally bolts hit enemies
      for (let ei = this.enemies.length - 1; ei >= 0; ei--) {
        if (spheresCollide(bolt, this.enemies[ei].mesh)) {
          this.damageEnemyAt(ei, 1);
          hit = true;
          break;
        }
      }
      // Ally bolts chip/destroy blocks
      if (!hit) {
        for (let i = this.obstacles.length - 1; i >= 0; i--) {
          if (spheresCollide(bolt, this.obstacles[i])) {
            this.damageObstacleAt(i, 1);
            hit = true;
            break;
          }
        }
      }
      if (hit) {
        this.ctx.scene.remove(bolt);
        disposeObject3D(bolt);
        continue;
      }

      const traveled = ship.position.distanceTo(bolt.position);
      if (traveled > RAIL_RANGE + 40) {
        this.ctx.scene.remove(bolt);
        disposeObject3D(bolt);
        continue;
      }
      remaining.push(bolt);
    }
    this.allyRails = remaining;
  }

  /**
   * Move projectiles via userData.velocity; damage obstacles and enemy.
   * Hard-locked missiles re-steer each frame toward the enemy.
   */
  private advanceProjectiles(
    list: THREE.Group[],
    dt: number,
    range: number,
    obstacleDamage: number,
    enemyDamage: number,
  ): THREE.Group[] {
    const ship = this.ship;
    if (!ship) return list;

    const remaining: THREE.Group[] = [];
    for (const proj of list) {
      // Enemy-lock missiles keep tracking while the foe is alive
      this.steerHomingMissile(proj, dt);

      const vel = proj.userData.velocity as THREE.Vector3 | undefined;
      if (vel) {
        proj.position.x += vel.x * dt;
        proj.position.y += vel.y * dt;
        proj.position.z += vel.z * dt;
      } else {
        proj.position.z -= MISSILE_SPEED * dt;
      }

      let hit = false;

      // Enemies first (homing shots and any proximity hit)
      if (!hit) {
        for (let ei = this.enemies.length - 1; ei >= 0; ei--) {
          if (spheresCollide(proj, this.enemies[ei].mesh)) {
            this.damageEnemyAt(ei, enemyDamage);
            hit = true;
            break;
          }
        }
      }

      // Friendly fire: player missiles/rails can kill wingmen
      if (!hit) {
        for (let ti = this.teammates.length - 1; ti >= 0; ti--) {
          if (spheresCollide(proj, this.teammates[ti].mesh)) {
            this.damageTeammateAt(ti, enemyDamage >= ENEMY_MAX_HP ? TEAMMATE_MAX_HP : 1);
            hit = true;
            break;
          }
        }
      }

      if (!hit) {
        for (let i = this.obstacles.length - 1; i >= 0; i--) {
          const cube = this.obstacles[i];
          if (spheresCollide(proj, cube)) {
            this.damageObstacleAt(i, obstacleDamage);
            hit = true;
            break;
          }
        }
      }

      if (hit) {
        this.ctx.scene.remove(proj);
        disposeObject3D(proj);
        continue;
      }

      const traveled = ship.position.distanceTo(proj.position);
      if (traveled > range || proj.position.z < ship.position.z - range) {
        this.ctx.scene.remove(proj);
        disposeObject3D(proj);
        continue;
      }

      remaining.push(proj);
    }
    return remaining;
  }

  private updateEnemyLock(dt: number) {
    if (this.enemies.length === 0) {
      this.dropEnemyLock({ requireRelease: false });
      return;
    }

    // Hard lock only while missiles are selected — railgun switch also clears via setWeapon
    if (this.weapon !== 'missile') {
      if (this.enemyHardLocked || this.enemyLockProgress > 0 || this.lockTarget) {
        this.dropEnemyLock({ requireRelease: false });
      }
      return;
    }

    // Resolve sticky pin against the live list (mesh identity — no re-score)
    const live = this.resolveLiveLockTarget();

    // Pin died / left engage: full drop. If the player was still holding or hard-locked,
    // force a release before any other foe can be acquired (no auto-transfer).
    if (this.lockTarget && !live) {
      const wasActive = this.lockHold || this.enemyHardLocked || this.enemyLockProgress > 0;
      this.dropEnemyLock({ requireRelease: wasActive });
      return;
    }

    // Far horizon approach foes are not lockable yet
    const hasEngageable = this.enemies.some((e) => e.phase === 'engage');
    if (!hasEngageable) {
      if (this.enemyHardLocked || this.enemyLockProgress > 0 || this.lockTarget) {
        this.dropEnemyLock({ requireRelease: false });
      }
      return;
    }

    // Hard lock: stick to the same pin for the rest of its life
    if (this.enemyHardLocked) {
      this.enemyLockProgress = 1;
      // live pin is guaranteed above; never pick another foe here
      return;
    }

    // Soft acquire: pin once on first frame of hold, then only advance progress
    if (this.lockHold && !this.lockReleaseRequired) {
      if (!this.lockTarget) {
        this.lockTarget = this.pickBestLockEnemy();
      }
      if (!this.lockTarget) {
        this.enemyLockProgress = 0;
        return;
      }
      // AIM-9 (F-22) locks faster than the default brick missiles
      const lockHold = this.getPlayerMissileLockHold();
      this.enemyLockProgress = Math.min(1, this.enemyLockProgress + dt / lockHold);
      if (this.enemyLockProgress >= 1) {
        this.enemyHardLocked = true;
        // Keep the same pin — do not re-pick "best" at hard-lock time
      }
      return;
    }

    // Not holding (and not hard-locked): clear soft progress + pin so HUD free-roams preview
    if (!this.lockHold && !this.enemyHardLocked) {
      this.enemyLockProgress = 0;
      this.lockTarget = null;
    }
  }

  /**
   * Sticky lock target while alive & engaged.
   * Never re-picks another foe; returns null if the pin is gone.
   */
  private getLockTargetEnemy(): EnemyState | null {
    return this.resolveLiveLockTarget();
  }

  /** Seconds to hard-lock: Sidewinder (F-22) is 1.5s, default missiles 2.5s. */
  private getPlayerMissileLockHold(): number {
    return this.shipId === 'f22' ? SIDEWINDER_LOCK_HOLD : ENEMY_LOCK_HOLD;
  }

  /** Live pin only if still in the enemy list and in engage phase. */
  private resolveLiveLockTarget(): EnemyState | null {
    const live = resolveLiveLockTargetAmong(this.lockTarget, this.enemies);
    // Keep lockTarget pointing at the live list entry (mesh identity)
    if (live) {
      this.lockTarget = live;
    }
    return live;
  }

  /**
   * Score foes for lock / soft HUD: prefer near screen center, then nearer to the ship.
   * Acquire (default): engage-phase only. Preview: any living foe so soft brackets show
   * while they approach (matches always-on single-enemy soft HUD).
   */
  private pickBestLockEnemy(opts?: { preview?: boolean }): EnemyState | null {
    if (!this.ship) {
      return null;
    }
    return pickBestLockEnemyAmong(this.ctx.camera, this.ship, this.enemies, opts);
  }

  /** Horizon foes: tiny + translucent at spawn, grow/fade in with approach ease (block-like). */
  private updateHorizonPresence(enemy: EnemyState, ease: number) {
    if (enemy.approachMode !== 'horizon') {
      return;
    }
    const e = clamp(ease, 0, 1);
    const scale = ENEMY_HORIZON_SCALE_MIN + (ENEMY_SCALE - ENEMY_HORIZON_SCALE_MIN) * e;
    enemy.mesh.scale.setScalar(scale);
    this.setEnemyOpacity(enemy.mesh, 0.12 + 0.88 * e);
  }

  private setEnemyOpacity(mesh: THREE.Object3D, opacity: number) {
    const a = clamp(opacity, 0, 1);
    mesh.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const apply = (mat: THREE.Material) => {
        if (mat instanceof THREE.MeshBasicMaterial) {
          mat.transparent = a < 0.999;
          mat.opacity = a;
          mat.depthWrite = a > 0.85;
        }
      };
      if (Array.isArray(child.material)) {
        child.material.forEach(apply);
      } else {
        apply(child.material);
      }
    });
  }

  private updateEnemy(dt: number) {
    const ship = this.ship;
    if (!ship || this.enemies.length === 0) return;

    // Copy list so destroy during update is safe
    for (const enemy of [...this.enemies]) {
      if (!this.enemies.includes(enemy)) continue;
      this.updateSingleEnemy(dt, enemy, ship);
    }
  }

  private updateSingleEnemy(dt: number, enemy: EnemyState, ship: THREE.Group) {
    const boundX = this.getBoundX();

    enemy.follow.x = damp(enemy.follow.x, ship.position.x, ENEMY_LATERAL_FOLLOW, dt);
    enemy.follow.y = damp(enemy.follow.y, ship.position.y, ENEMY_LATERAL_FOLLOW, dt);
    enemy.follow.z = ship.position.z;

    // Block dodge
    let avoidX = 0;
    let avoidY = 0;
    for (const cube of this.obstacles) {
      const distAhead = enemy.mesh.position.z - cube.position.z;
      if (distAhead < 4 || distAhead > 100) continue;
      const dx = cube.position.x - enemy.mesh.position.x;
      const dy = cube.position.y - enemy.mesh.position.y;
      const lat = Math.hypot(dx, dy);
      const threatR = (typeof cube.userData.radius === 'number' ? cube.userData.radius : 4) * 2.4;
      if (lat < threatR + 5) {
        const push = (threatR + 5 - lat) / (threatR + 5);
        avoidX -= Math.sign(dx || 1) * push * 22;
        avoidY -= Math.sign(dy || 1) * push * 14;
      }
    }

    // Separation from other foes + ally (friendly-fire / collision avoidance)
    for (const other of this.enemies) {
      if (other === enemy) continue;
      const dx = enemy.mesh.position.x - other.mesh.position.x;
      const dy = enemy.mesh.position.y - other.mesh.position.y;
      const dz = enemy.mesh.position.z - other.mesh.position.z;
      const lat = Math.hypot(dx, dy);
      const sep = 14;
      if (lat < sep && Math.abs(dz) < 40) {
        const push = (sep - lat) / sep;
        avoidX += Math.sign(dx || 1) * push * 18;
        avoidY += Math.sign(dy || 1) * push * 10;
      }
    }
    for (const mate of this.teammates) {
      const m = mate.mesh.position;
      const dx = enemy.mesh.position.x - m.x;
      const dy = enemy.mesh.position.y - m.y;
      const lat = Math.hypot(dx, dy);
      if (lat < 16) {
        const push = (16 - lat) / 16;
        avoidX += Math.sign(dx || 1) * push * 14;
        avoidY += Math.sign(dy || 1) * push * 10;
      }
    }

    // Nudge off player bolt corridor when a rail is near (simple dodge)
    for (const bolt of this.rails) {
      const dx = enemy.mesh.position.x - bolt.position.x;
      const dy = enemy.mesh.position.y - bolt.position.y;
      const dz = enemy.mesh.position.z - bolt.position.z;
      // Player rails fly −Z; only care if bolt is roughly behind/at us on Z and near laterally
      if (dz > -8 && dz < 50) {
        const lat = Math.hypot(dx, dy);
        if (lat < 7) {
          avoidX += Math.sign(dx || 1) * (7 - lat) * 3;
          avoidY += Math.sign(dy || 1) * (7 - lat) * 2;
        }
      }
    }

    enemy.avoidX = damp(enemy.avoidX, clamp(avoidX, -26, 26), 2.5, dt);
    enemy.avoidY = damp(enemy.avoidY, clamp(avoidY, -14, 14), 2.5, dt);

    enemy.roamRetarget -= dt;
    if (enemy.roamRetarget <= 0) {
      let nextX = enemy.follow.x + enemy.slotX + (Math.random() * 2 - 1) * ROAM_STEP_MAX * 0.65;
      let nextY = enemy.follow.y + enemy.slotY + (Math.random() * 2 - 1) * ROAM_STEP_MAX * 0.4;
      if (this.teammates.length > 0 && Math.random() < 0.4) {
        const focus = this.teammates[Math.floor(Math.random() * this.teammates.length)];
        nextX = focus.mesh.position.x * 0.55 + enemy.mesh.position.x * 0.45;
        nextY = focus.mesh.position.y * 0.5 + enemy.mesh.position.y * 0.5;
        nextX += (Math.random() * 2 - 1) * 5;
        nextY += (Math.random() * 2 - 1) * 3;
      }
      enemy.roamX = clamp(nextX, -boundX * 0.95, boundX * 0.95);
      enemy.roamY = clamp(nextY, SHIP_Y_MIN, SHIP_Y_MAX);
      enemy.roamRetarget = ENEMY_ROAM_MIN + Math.random() * (ENEMY_ROAM_MAX - ENEMY_ROAM_MIN);
    }

    const targetX = clamp(enemy.roamX + enemy.avoidX * 0.85, -boundX * 0.95, boundX * 0.95);
    const targetY = clamp(enemy.roamY + enemy.avoidY * 0.75, SHIP_Y_MIN, SHIP_Y_MAX);
    const engageAhead = ENEMY_AHEAD + enemy.engageExtra;
    const targetZ = ship.position.z - engageAhead;
    const approachDur =
      enemy.approachMode === 'horizon' ? ENEMY_HORIZON_APPROACH_TIME : ENEMY_APPROACH_TIME;
    const dampXY = enemy.phase === 'approach' ? ENEMY_LATERAL_DAMP * 1.05 : ENEMY_LATERAL_DAMP;

    if (enemy.phase === 'approach') {
      enemy.approachT = Math.min(1, enemy.approachT + dt / approachDur);
      const t = enemy.approachT;
      const ease = t * t * (3 - 2 * t);

      // Lateral ease toward formation
      enemy.mesh.position.x = damp(enemy.mesh.position.x, targetX, dampXY, dt);
      enemy.mesh.position.y = damp(enemy.mesh.position.y, targetY, dampXY, dt);

      if (enemy.approachMode === 'horizon') {
        // Explicit far→near travel relative to the live ship (block-like close-in).
        // Avoid free damp chasing a moving Z target (that never reached engage).
        const farAhead = ENEMY_HORIZON_SPAWN + enemy.engageExtra * 0.5;
        const ahead = farAhead + (engageAhead - farAhead) * ease;
        enemy.mesh.position.z = ship.position.z - ahead;
        this.updateHorizonPresence(enemy, ease);
        enemy.mesh.rotation.x = damp(enemy.mesh.rotation.x, 0, 4, dt);
        enemy.mesh.rotation.y = damp(enemy.mesh.rotation.y, 0, 3.2, dt);
        enemy.mesh.rotation.z = damp(enemy.mesh.rotation.z, -enemy.avoidX * 0.02, 3.5, dt);
      } else {
        enemy.mesh.position.z = damp(enemy.mesh.position.z, targetZ, ENEMY_Z_DAMP * 1.25, dt);
        const faceBack = t > 0.45 ? (t - 0.45) / 0.55 : 0;
        enemy.mesh.rotation.x = damp(enemy.mesh.rotation.x, 0, 4, dt);
        enemy.mesh.rotation.y = damp(enemy.mesh.rotation.y, Math.PI * (1 - faceBack), 3.2, dt);
        enemy.mesh.rotation.z = damp(enemy.mesh.rotation.z, -enemy.avoidX * 0.02, 3.5, dt);
      }

      if (t >= 1) {
        enemy.phase = 'engage';
        enemy.mesh.position.z = targetZ;
        enemy.mesh.scale.setScalar(ENEMY_SCALE);
        this.setEnemyOpacity(enemy.mesh, 1);
        enemy.shootCooldown = Math.max(enemy.shootCooldown, 0.35 + Math.random() * 0.4);
      }
      return;
    }

    // Engage: hold ring distance, full visibility
    enemy.mesh.position.x = damp(enemy.mesh.position.x, targetX, dampXY, dt);
    enemy.mesh.position.y = damp(enemy.mesh.position.y, targetY, dampXY, dt);
    enemy.mesh.position.z = damp(enemy.mesh.position.z, targetZ, ENEMY_Z_DAMP, dt);
    enemy.mesh.scale.setScalar(ENEMY_SCALE);
    this.setEnemyOpacity(enemy.mesh, 1);

    enemy.mesh.rotation.x = damp(enemy.mesh.rotation.x, 0, 4, dt);
    enemy.mesh.rotation.y = damp(enemy.mesh.rotation.y, 0, 4, dt);
    enemy.mesh.rotation.z = damp(enemy.mesh.rotation.z, -enemy.avoidX * 0.025, 3.5, dt);

    // Missile-capable foes: missiles only (never rail bursts)
    if (enemy.canMissile) {
      enemy.burstLeft = 0;
      if (enemy.missileCooldown > 0) {
        enemy.missileCooldown = Math.max(0, enemy.missileCooldown - dt);
        enemy.missileLockProgress = 0;
      } else {
        // Ready to lock: hold 3s on the player, then launch one missile
        enemy.missileLockProgress = Math.min(
          1,
          enemy.missileLockProgress + dt / ENEMY_MISSILE_LOCK_HOLD,
        );
        if (enemy.missileLockProgress >= 1) {
          this.spawnEnemyMissile(enemy);
          enemy.missileCooldown = ENEMY_MISSILE_COOLDOWN;
          enemy.missileLockProgress = 0;
        }
      }
      return;
    }

    enemy.missileLockProgress = 0;
    enemy.shootCooldown -= dt;
    if (enemy.burstLeft > 0) {
      enemy.burstTimer -= dt;
      if (enemy.burstTimer <= 0) {
        this.spawnEnemyBolt(enemy);
        enemy.burstLeft -= 1;
        enemy.burstTimer = ENEMY_BURST_GAP;
      }
    } else if (enemy.shootCooldown <= 0) {
      if (!this.enemyHasForwardTarget(enemy)) {
        // Nudge toward player lane so they can line up a straight shot
        enemy.roamX = damp(enemy.roamX, ship.position.x, 2.5, dt);
        enemy.roamY = damp(enemy.roamY, ship.position.y, 2.5, dt);
        enemy.shootCooldown = 0.2;
      } else {
        enemy.burstLeft = ENEMY_BURST_COUNT;
        enemy.burstTimer = 0;
        enemy.shootCooldown =
          ENEMY_SHOOT_INTERVAL_MIN +
          Math.random() * (ENEMY_SHOOT_INTERVAL_MAX - ENEMY_SHOOT_INTERVAL_MIN);
      }
    }
  }

  private enemyHasForwardTarget(enemy: EnemyState): boolean {
    const ship = this.ship;
    if (!ship) return false;

    // Wide enough that fanned-out foes still fire while they drift into lane
    const lane = 22;

    for (const mate of this.teammates) {
      const m = mate.mesh.position;
      const behind = m.z - enemy.mesh.position.z;
      const lat = Math.hypot(m.x - enemy.mesh.position.x, m.y - enemy.mesh.position.y);
      if (behind > 4 && behind < 120 && lat < lane) {
        return true;
      }
    }

    const behindP = ship.position.z - enemy.mesh.position.z;
    const latP = Math.hypot(
      ship.position.x - enemy.mesh.position.x,
      ship.position.y - enemy.mesh.position.y,
    );
    // Must be ahead of the player and roughly in the rear firing cone
    return behindP > 10 && behindP < 100 && latP < lane;
  }

  private spawnEnemyBolt(enemy: EnemyState) {
    const bolt = createRailBolt();
    bolt.scale.setScalar(RAIL_SCALE * 0.95);
    bolt.position.set(enemy.mesh.position.x, enemy.mesh.position.y, enemy.mesh.position.z + 2.2);
    // Straight +Z down the corridor (same as original rail salvos — not player-seeking)
    bolt.userData.velocity = new THREE.Vector3(0, 0, ENEMY_BOLT_SPEED);
    bolt.userData.fromEnemy = true;
    bolt.userData.ownerMesh = enemy.mesh;
    bolt.rotation.set(0, 0, 0);
    bolt.traverse((c) => {
      if (c instanceof THREE.Mesh && c.material instanceof THREE.MeshBasicMaterial) {
        c.material = c.material.clone();
        c.material.color.setHex(0xff8a65);
      }
    });
    this.ctx.scene.add(bolt);
    this.enemyRails.push(bolt);
  }

  /** Fire a lock-on missile at chaff if live, otherwise the player. */
  private spawnEnemyMissile(enemy: EnemyState) {
    const missile = createEnemyMissile();
    missile.scale.setScalar(ENEMY_MISSILE_SCALE);
    missile.position.set(enemy.mesh.position.x, enemy.mesh.position.y, enemy.mesh.position.z + 2.6);
    // Pin nearest chaff immediately so the shot commits to the decoy
    const decoy = this.getChaffDecoy(missile.position);
    missile.userData.decoyPod = decoy;
    const aim =
      decoy?.position.clone() ??
      this.ship?.position.clone() ??
      new THREE.Vector3(enemy.mesh.position.x, enemy.mesh.position.y, enemy.mesh.position.z + 40);
    const dir = new THREE.Vector3().subVectors(aim, missile.position);
    if (dir.lengthSq() < 0.0001) {
      dir.set(0, 0, 1);
    } else {
      dir.normalize();
    }
    missile.userData.velocity = dir.multiplyScalar(ENEMY_MISSILE_SPEED);
    missile.userData.fromEnemy = true;
    missile.userData.ownerMesh = enemy.mesh;
    missile.userData.homing = true;
    missile.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir.clone().normalize());
    this.ctx.scene.add(missile);
    this.enemyMissiles.push(missile);
  }

  /**
   * Hostile missiles: home hard on chaff when present (sticky decoy), else the player.
   * Reaching a chaff catch radius destroys the missile there (pod pops for feedback).
   */
  private updateEnemyMissiles(dt: number) {
    const ship = this.ship;
    if (!ship) return;
    const remaining: THREE.Group[] = [];
    for (const missile of this.enemyMissiles) {
      const vel = missile.userData.velocity as THREE.Vector3 | undefined;
      const decoy = this.resolveMissileDecoy(missile);
      if (vel) {
        const aim = this.getEnemyMissileAimPoint(missile);
        if (aim) {
          const toTarget = new THREE.Vector3().subVectors(aim, missile.position);
          if (toTarget.lengthSq() > 0.0001) {
            toTarget.normalize();
            // Snappier turn when chasing a decoy so the missile commits to chaff
            const homing = decoy ? ENEMY_MISSILE_HOMING * 1.35 : ENEMY_MISSILE_HOMING;
            const turn = 1 - Math.exp(-homing * dt);
            const desired = toTarget.multiplyScalar(ENEMY_MISSILE_SPEED);
            vel.lerp(desired, turn);
            if (vel.lengthSq() > 0.0001) {
              vel.normalize().multiplyScalar(ENEMY_MISSILE_SPEED);
              missile.quaternion.setFromUnitVectors(
                new THREE.Vector3(0, 0, 1),
                vel.clone().normalize(),
              );
            }
          }
        }
        missile.position.addScaledVector(vel, dt);
      }

      let consumed = false;

      // Decoy: missile is destroyed at chaff (generous catch radius, not mesh-only)
      for (let ci = this.chaffPods.length - 1; ci >= 0; ci--) {
        const pod = this.chaffPods[ci];
        const mr = typeof missile.userData.radius === 'number' ? missile.userData.radius : 1;
        if (missile.position.distanceTo(pod.position) <= CHAFF_MISSILE_CATCH_RADIUS + mr) {
          this.destroyChaffPod(pod);
          consumed = true;
          break;
        }
      }
      if (consumed) {
        this.ctx.scene.remove(missile);
        disposeObject3D(missile);
        continue;
      }

      // Splash into obstacles
      for (let i = this.obstacles.length - 1; i >= 0; i--) {
        if (spheresCollide(missile, this.obstacles[i])) {
          this.damageObstacleAt(i, OBSTACLE_MAX_DAMAGE);
          consumed = true;
          break;
        }
      }
      if (consumed) {
        this.ctx.scene.remove(missile);
        disposeObject3D(missile);
        continue;
      }

      if (ship.position.distanceTo(missile.position) > ENEMY_MISSILE_RANGE) {
        this.ctx.scene.remove(missile);
        disposeObject3D(missile);
        continue;
      }
      remaining.push(missile);
    }
    this.enemyMissiles = remaining;
  }

  private checkEnemyMissileHitsPlayer(): boolean {
    const ship = this.ship;
    if (!ship) return false;
    for (let i = this.enemyMissiles.length - 1; i >= 0; i--) {
      const missile = this.enemyMissiles[i];
      if (spheresCollide(missile, ship)) {
        this.ctx.scene.remove(missile);
        disposeObject3D(missile);
        this.enemyMissiles.splice(i, 1);
        return true;
      }
    }
    return false;
  }

  private damageEnemyAt(index: number, amount: number) {
    const enemy = this.enemies[index];
    if (!enemy) return;
    enemy.hp -= amount;
    enemy.mesh.scale.setScalar(ENEMY_SCALE * (1.12 + Math.random() * 0.06));
    if (enemy.hp <= 0) {
      this.destroyEnemyAt(index);
      return;
    }
    enemy.mesh.scale.setScalar(ENEMY_SCALE);
  }

  private destroyEnemyAt(index: number) {
    const enemy = this.enemies[index];
    if (!enemy) return;

    const dummy = new THREE.Mesh(
      new THREE.BoxGeometry(4, 4, 4),
      new THREE.MeshBasicMaterial({ color: 0x6a717c }),
    );
    dummy.position.copy(enemy.mesh.position);
    dummy.userData.size = 5;
    dummy.userData.radius = 2.6;
    this.spawnDebrisBurst(dummy, ENEMY_DEBRIS_PALETTE);
    dummy.geometry.dispose();
    (dummy.material as THREE.MeshBasicMaterial).dispose();

    this.ctx.scene.remove(enemy.mesh);
    disposeObject3D(enemy.mesh);

    const wasLock =
      this.lockTarget === enemy ||
      (this.lockTarget !== null && this.lockTarget.mesh === enemy.mesh);
    this.enemies.splice(index, 1);
    if (wasLock) {
      // Never auto-lock the next foe — player must release and hold again
      this.dropEnemyLock({ requireRelease: true });
    } else if (this.lockTarget && !this.enemies.some((e) => e.mesh === this.lockTarget!.mesh)) {
      // Pin orphaned somehow — drop without transferring
      this.dropEnemyLock({ requireRelease: true });
    }

    if (this.enemies.length === 0) {
      this.dropEnemyLock({ requireRelease: false });
      this.lockReleaseRequired = false;
      this.callbacks.onEnemyChange?.(false);
    } else {
      this.callbacks.onEnemyChange?.(true);
    }
  }

  private updateTeammate(dt: number) {
    const ship = this.ship;
    if (!ship || this.teammates.length === 0) return;

    for (const mate of [...this.teammates]) {
      if (!this.teammates.includes(mate)) continue;
      this.updateSingleTeammate(dt, mate, ship);
    }
  }

  private updateSingleTeammate(dt: number, mate: TeammateState, ship: THREE.Group) {
    const boundX = this.getBoundX();

    // Obstacle dodge — same idea as enemy, stronger push off crash path
    let avoidX = 0;
    let avoidY = 0;
    for (const cube of this.obstacles) {
      // Corridor forward is -Z: cube is ahead of wingman when cube.z < mate.z
      const distAhead = mate.mesh.position.z - cube.position.z;
      if (distAhead < 4 || distAhead > 100) continue;
      const dx = cube.position.x - mate.mesh.position.x;
      const dy = cube.position.y - mate.mesh.position.y;
      const lat = Math.hypot(dx, dy);
      const threatR = (typeof cube.userData.radius === 'number' ? cube.userData.radius : 4) * 2.4;
      if (lat < threatR + 5) {
        const push = (threatR + 5 - lat) / (threatR + 5);
        avoidX -= Math.sign(dx || 1) * push * 22;
        avoidY -= Math.sign(dy || 1) * push * 14;
      }
    }
    // Separation from other allies so they don’t stack
    for (const other of this.teammates) {
      if (other === mate) continue;
      const dx = mate.mesh.position.x - other.mesh.position.x;
      const dy = mate.mesh.position.y - other.mesh.position.y;
      const dz = mate.mesh.position.z - other.mesh.position.z;
      const lat = Math.hypot(dx, dy);
      const sep = 12;
      if (lat < sep && Math.abs(dz) < 30) {
        const push = (sep - lat) / sep;
        avoidX += Math.sign(dx || 1) * push * 16;
        avoidY += Math.sign(dy || 1) * push * 10;
      }
    }
    mate.avoidX = damp(mate.avoidX, clamp(avoidX, -24, 24), 2.4, dt);
    mate.avoidY = damp(mate.avoidY, clamp(avoidY, -12, 12), 2.4, dt);

    // Nearby roam steps only (no full-box teleport targets) — bias toward formation slot
    mate.roamRetarget -= dt;
    if (mate.roamRetarget <= 0) {
      mate.roamX = clamp(
        ship.position.x + mate.slotX + (Math.random() * 2 - 1) * ROAM_STEP_MAX * 0.7,
        -boundX,
        boundX,
      );
      mate.roamY = clamp(
        ship.position.y + mate.slotY + (Math.random() * 2 - 1) * (ROAM_STEP_MAX * 0.45),
        SHIP_Y_MIN,
        SHIP_Y_MAX,
      );
      mate.roamRetarget =
        TEAMMATE_ROAM_MIN + Math.random() * (TEAMMATE_ROAM_MAX - TEAMMATE_ROAM_MIN);
    }

    // Always damp toward slot — never hard-set (fixes approach teleports)
    const targetX = clamp(mate.roamX + mate.avoidX * 0.85, -boundX, boundX);
    const targetY = clamp(mate.roamY + mate.avoidY * 0.7, SHIP_Y_MIN, SHIP_Y_MAX);
    const targetZ = ship.position.z - (TEAMMATE_AHEAD + mate.engageExtra);
    const dampXY = mate.phase === 'approach' ? TEAMMATE_LATERAL_DAMP * 1.15 : TEAMMATE_LATERAL_DAMP;
    const dampZ = mate.phase === 'approach' ? TEAMMATE_Z_DAMP * 1.3 : TEAMMATE_Z_DAMP;
    mate.mesh.position.x = damp(mate.mesh.position.x, targetX, dampXY, dt);
    mate.mesh.position.y = damp(mate.mesh.position.y, targetY, dampXY, dt);
    mate.mesh.position.z = damp(mate.mesh.position.z, targetZ, dampZ, dt);

    if (mate.phase === 'approach') {
      mate.approachT = Math.min(1, mate.approachT + dt / TEAMMATE_APPROACH_TIME);
      mate.mesh.rotation.y = Math.PI;
      mate.mesh.rotation.x = damp(mate.mesh.rotation.x, 0, 5, dt);
      mate.mesh.rotation.z = damp(mate.mesh.rotation.z, -mate.avoidX * 0.02, 4, dt);
      if (mate.approachT >= 1) {
        mate.phase = 'engage';
        mate.shootCooldown = 1.2 + Math.random() * 0.8;
      }
      return;
    }

    // Face down the corridor like the player; bank into dodge
    mate.mesh.rotation.x = damp(mate.mesh.rotation.x, 0, 5, dt);
    mate.mesh.rotation.y = Math.PI;
    mate.mesh.rotation.z = damp(mate.mesh.rotation.z, -mate.avoidX * 0.028, 4, dt);

    // Semi-auto straight rail bursts when something is in the forward lane
    mate.shootCooldown -= dt;
    if (mate.burstLeft > 0) {
      mate.burstTimer -= dt;
      if (mate.burstTimer <= 0) {
        this.spawnTeammateBolt(mate);
        mate.burstLeft -= 1;
        mate.burstTimer = TEAMMATE_BURST_GAP;
      }
    } else if (mate.shootCooldown <= 0) {
      if (!this.teammateHasForwardTarget(mate)) {
        mate.shootCooldown = 0.4;
      } else {
        mate.burstLeft = TEAMMATE_BURST_COUNT;
        mate.burstTimer = 0;
        mate.shootCooldown =
          TEAMMATE_SHOOT_INTERVAL_MIN +
          Math.random() * (TEAMMATE_SHOOT_INTERVAL_MAX - TEAMMATE_SHOOT_INTERVAL_MIN);
      }
    }
  }

  /**
   * True when enemy or a block sits roughly in the wingman’s forward lane
   * (straight -Z fire will have a chance to connect).
   */
  private teammateHasForwardTarget(mate: TeammateState): boolean {
    const lane = 7.5;

    for (const foe of this.enemies) {
      const epos = foe.mesh.position;
      const ahead = mate.mesh.position.z - epos.z;
      const lat = Math.hypot(epos.x - mate.mesh.position.x, epos.y - mate.mesh.position.y);
      if (ahead > 6 && ahead < 150 && lat < lane + 4) {
        return true;
      }
    }

    for (const cube of this.obstacles) {
      const ahead = mate.mesh.position.z - cube.position.z;
      if (ahead < 8 || ahead > 160) continue;
      const lat = Math.hypot(
        cube.position.x - mate.mesh.position.x,
        cube.position.y - mate.mesh.position.y,
      );
      const r = typeof cube.userData.radius === 'number' ? cube.userData.radius : 4;
      if (lat < lane + r * 0.6) {
        return true;
      }
    }
    return false;
  }

  private spawnTeammateBolt(mate: TeammateState) {
    // Straight corridor shot (same as player rail) — no aim/tracking toward targets
    const bolt = createRailBolt();
    bolt.scale.setScalar(RAIL_SCALE * 0.9);
    bolt.rotation.y = Math.PI;
    bolt.position.set(mate.mesh.position.x, mate.mesh.position.y, mate.mesh.position.z - 2.5);
    bolt.userData.velocity = new THREE.Vector3(0, 0, -TEAMMATE_BOLT_SPEED);
    bolt.userData.fromAlly = true;
    this.ctx.scene.add(bolt);
    this.allyRails.push(bolt);
  }

  private damageTeammateAt(index: number, amount: number) {
    const mate = this.teammates[index];
    if (!mate) return;
    mate.hp -= amount;
    mate.mesh.scale.setScalar(TEAMMATE_SCALE * (1.1 + Math.random() * 0.05));
    if (mate.hp <= 0) {
      this.destroyTeammateAt(index);
      return;
    }
    mate.mesh.scale.setScalar(TEAMMATE_SCALE);
  }

  private destroyTeammateAt(index: number) {
    const mate = this.teammates[index];
    if (!mate) return;
    const dummy = new THREE.Mesh(
      new THREE.BoxGeometry(4, 4, 4),
      new THREE.MeshBasicMaterial({ color: 0xc62828 }),
    );
    dummy.position.copy(mate.mesh.position);
    dummy.userData.size = 5;
    dummy.userData.radius = 2.4;
    this.spawnDebrisBurst(dummy, TEAMMATE_DEBRIS_PALETTE);
    dummy.geometry.dispose();
    (dummy.material as THREE.MeshBasicMaterial).dispose();

    this.ctx.scene.remove(mate.mesh);
    disposeObject3D(mate.mesh);
    this.teammates.splice(index, 1);

    if (this.teammates.length === 0) {
      this.callbacks.onTeammateChange?.(false);
    } else {
      this.callbacks.onTeammateChange?.(true);
    }
  }

  /** Apply hits; destroy + debris at OBSTACLE_MAX_DAMAGE. */
  private damageObstacleAt(index: number, amount: number) {
    const cube = this.obstacles[index];
    if (!cube) return;

    const prev = typeof cube.userData.damage === 'number' ? cube.userData.damage : 0;
    const next = prev + amount;
    if (next >= OBSTACLE_MAX_DAMAGE) {
      this.destroyObstacleAt(index);
      return;
    }
    applyObstacleDamageVisual(cube, next);
  }

  private destroyObstacleAt(index: number) {
    const cube = this.obstacles[index];
    if (!cube) return;

    // Burst into flying chunks before removing the solid obstacle
    this.spawnDebrisBurst(cube);

    this.ctx.scene.remove(cube);
    // Dispose cracks + cube
    disposeObject3D(cube);
    this.obstacles.splice(index, 1);

    // Regular: independent loot rolls after 30s
    if (!this.sandbox && this.elapsed >= REGULAR_LOOT_START) {
      this.applyRegularBlockLoot();
    }
  }

  /** Independent loot table — see docs/regular-play-mode.md §7.2. */
  private applyRegularBlockLoot() {
    const loot = rollRegularBlockLoot();
    let ammoChanged = false;
    if (loot.rail > 0) {
      this.railAmmo = Math.min(RAILGUN_MAX, this.railAmmo + loot.rail);
      this.callbacks.onRailgunAmmoChange?.(this.railAmmo, RAILGUN_MAX);
      ammoChanged = true;
    }
    if (loot.missile > 0) {
      this.ammo = Math.min(MISSILE_MAX, this.ammo + loot.missile);
      this.callbacks.onAmmoChange?.(this.ammo, MISSILE_MAX);
      ammoChanged = true;
    }
    if (loot.chaff > 0) {
      this.chaffAmmo = Math.min(CHAFF_MAX, this.chaffAmmo + loot.chaff);
      this.callbacks.onChaffAmmoChange?.(this.chaffAmmo, CHAFF_MAX);
      ammoChanged = true;
    }
    if (loot.allies > 0) {
      this.addTeammates(loot.allies);
    }
    void ammoChanged;
  }

  /**
   * Regular timeline: block cap, one-shot intro enemy/ally, endless waves after 38s.
   * See docs/regular-play-mode.md.
   */
  private tickRegularDirector() {
    const dir = this.regular;
    if (!dir || this.phase !== 'playing') {
      return;
    }
    const t = this.elapsed;

    // Keep cube count under the timeline cap (drops from 2→1 at 30s, dual pulses later)
    this.trimObstaclesTo(regularObstacleLimit(t));

    // Intro enemy (rail-only) once in [10, 20)
    if (!dir.spawnedIntroEnemy && t >= dir.introEnemyAt) {
      if (this.spawnEnemyFormation(1, { forceRailOnly: true })) {
        dir.spawnedIntroEnemy = true;
      }
    }

    // Intro ally once in [20, 25); retry until window ends if spawn failed
    if (!dir.spawnedIntroAlly && t >= dir.introAllyAt && t < REGULAR_INTRO_ALLY_END) {
      if (this.addTeammates(1) > 0) {
        dir.spawnedIntroAlly = true;
      }
    }

    // Endless waves from 38s: clear → wait 3s → random 2/3/4
    if (t >= REGULAR_WAVE_START) {
      if (this.enemies.length > 0) {
        dir.nextWaveAt = null;
      } else if (dir.nextWaveAt === null) {
        dir.nextWaveAt = t + REGULAR_WAVE_CLEAR_DELAY;
      } else if (t >= dir.nextWaveAt) {
        this.spawnEnemyFormation(pickRegularWaveSize(), { forceRailOnly: false });
        dir.nextWaveAt = null;
      }
    }
  }

  /**
   * Scatter small colored bricks outward from the hit mesh.
   * Count, directions, spin, and speeds are randomized per blast.
   * Optional palette overrides the default pastel obstacle colors.
   */
  private spawnDebrisBurst(cube: THREE.Mesh, palette?: readonly number[]) {
    const origin = cube.position.clone();
    const worldScale = cube.scale.x;
    // Radius is set on spawn; scale grows as obstacles approach
    const radius = typeof cube.userData.radius === 'number' ? cube.userData.radius : 4;
    const approxSize = radius * 1.5 * worldScale;

    const count = DEBRIS_MIN + Math.floor(Math.random() * (DEBRIS_MAX - DEBRIS_MIN + 1));
    const colors = palette ?? OBSTACLE_DEBRIS_PALETTE;

    for (let i = 0; i < count; i++) {
      const chunkSize = approxSize * (0.12 + Math.random() * 0.22);
      const color = colors[Math.floor(Math.random() * colors.length)];
      const mesh = new THREE.Mesh(
        new THREE.BoxGeometry(chunkSize, chunkSize * (0.7 + Math.random() * 0.6), chunkSize),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 1 }),
      );
      mesh.position.copy(origin);
      mesh.position.x += (Math.random() * 2 - 1) * approxSize * 0.15;
      mesh.position.y += (Math.random() * 2 - 1) * approxSize * 0.15;
      mesh.position.z += (Math.random() * 2 - 1) * approxSize * 0.15;
      mesh.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);

      // Outward blast — mostly away from ship (more negative Z) with random spread
      const speed = 18 + Math.random() * 42;
      const dir = new THREE.Vector3(
        (Math.random() * 2 - 1) * 1.1,
        (Math.random() * 2 - 1) * 1.0 + 0.25,
        -0.55 - Math.random() * 1.2,
      ).normalize();

      const life = DEBRIS_LIFE * (0.65 + Math.random() * 0.55);
      this.ctx.scene.add(mesh);
      this.debris.push({
        mesh,
        velocity: dir.multiplyScalar(speed),
        spin: new THREE.Vector3(
          (Math.random() * 2 - 1) * 8,
          (Math.random() * 2 - 1) * 8,
          (Math.random() * 2 - 1) * 8,
        ),
        life,
        maxLife: life,
      });
    }
  }

  private updateDebris(dt: number) {
    const remaining: DebrisPiece[] = [];
    for (const piece of this.debris) {
      piece.life -= dt;
      if (piece.life <= 0) {
        this.ctx.scene.remove(piece.mesh);
        piece.mesh.geometry.dispose();
        (piece.mesh.material as THREE.MeshBasicMaterial).dispose();
        continue;
      }

      // Gravity-ish fall + drag
      piece.velocity.y -= 22 * dt;
      piece.velocity.multiplyScalar(1 - 0.55 * dt);
      piece.mesh.position.x += piece.velocity.x * dt;
      piece.mesh.position.y += piece.velocity.y * dt;
      piece.mesh.position.z += piece.velocity.z * dt;
      piece.mesh.rotation.x += piece.spin.x * dt;
      piece.mesh.rotation.y += piece.spin.y * dt;
      piece.mesh.rotation.z += piece.spin.z * dt;

      const t = piece.life / piece.maxLife;
      const mat = piece.mesh.material as THREE.MeshBasicMaterial;
      mat.opacity = Math.max(0, t);
      piece.mesh.scale.setScalar(0.55 + t * 0.55);

      remaining.push(piece);
    }
    this.debris = remaining;
  }

  private updateShip(dt: number) {
    const ship = this.ship;
    if (!ship) return;

    let dx = 0;
    let dy = 0;
    if (this.keys.has('ArrowLeft') || this.keys.has('KeyA')) dx -= 1;
    if (this.keys.has('ArrowRight') || this.keys.has('KeyD')) dx += 1;
    if (this.keys.has('ArrowUp') || this.keys.has('KeyW')) dy += 1;
    if (this.keys.has('ArrowDown') || this.keys.has('KeyS')) dy -= 1;

    const keyed = dx !== 0 || dy !== 0;
    if (keyed) {
      const len = Math.hypot(dx, dy) || 1;
      dx /= len;
      dy /= len;
    } else {
      // Gyro / tilt analog stick when no keyboard input
      const tx = Math.abs(this.tilt.x) > TILT_DEADZONE ? this.tilt.x : 0;
      const ty = Math.abs(this.tilt.y) > TILT_DEADZONE ? this.tilt.y : 0;
      dx = tx;
      dy = ty;
    }

    const boundX = this.getBoundX();
    ship.position.x = clamp(ship.position.x + dx * STRAFE_SPEED * dt, -boundX, boundX);
    // If the viewport got narrower (rotate phone), pull ship back inside
    ship.position.x = clamp(ship.position.x, -boundX, boundX);
    // Stay strictly between floor / ceiling infinite grids (no clipping through)
    ship.position.y = clamp(ship.position.y + dy * VERT_SPEED * dt, SHIP_Y_MIN, SHIP_Y_MAX);
    // Continuous forward travel along corridor
    ship.position.z -= SHIP_SPEED * dt;

    // Bank into strafe
    ship.rotation.z = damp(ship.rotation.z, -dx * 0.35, 8, dt);
    ship.rotation.x = damp(ship.rotation.x, dy * 0.12, 8, dt);
  }

  private updateObstacles(dt: number) {
    const ship = this.ship;
    if (!ship) return;

    const speed = OBSTACLE_BASE_SPEED + this.elapsed * 1.5;
    const remaining: THREE.Mesh[] = [];

    for (const cube of this.obstacles) {
      // Approach from horizon (more negative Z) toward / past the ship (+Z relative)
      cube.position.z += speed * dt;
      const spin = cube.userData.spin as { x: number; y: number };
      cube.rotation.x += spin.x;
      cube.rotation.y += spin.y;

      // Subtle scale-up as they approach (extra punch beyond perspective)
      const dist = cube.position.z - ship.position.z;
      // dist negative = still ahead; near 0 = at ship
      const t = clamp(1 - Math.abs(dist) / SPAWN_AHEAD, 0.35, 1.15);
      cube.scale.setScalar(0.75 + t * 0.55);

      if (cube.position.z < ship.position.z + DESPAWN_BEHIND) {
        remaining.push(cube);
      } else {
        this.ctx.scene.remove(cube);
        cube.geometry.dispose();
        const mats = cube.material;
        if (Array.isArray(mats)) {
          mats.forEach((m) => m.dispose());
        } else {
          mats.dispose();
        }
      }
    }

    this.obstacles = remaining;
  }

  private spawnObstacle() {
    const ship = this.ship;
    if (!ship) return;

    const cube = createObstacleCube(5 + Math.random() * 4);
    const ySpan = (SHIP_Y_MAX - SHIP_Y_MIN) * 0.85;
    const yMid = (SHIP_Y_MIN + SHIP_Y_MAX) / 2;
    const boundX = this.getBoundX();
    cube.position.set(
      (Math.random() * 2 - 1) * boundX * 0.95,
      yMid + (Math.random() * 2 - 1) * (ySpan / 2),
      ship.position.z - SPAWN_AHEAD,
    );
    cube.scale.setScalar(0.4);
    this.ctx.scene.add(cube);
    this.obstacles.push(cube);
  }

  private updateChaseCamera(mouse: THREE.Vector2, cameraCenter: THREE.Vector3) {
    const ship = this.ship;
    if (!ship) return;

    const cam = this.ctx.camera;
    // Behind ship (+Z local after ship faces -Z), slightly above
    const desired = new THREE.Vector3(
      ship.position.x * 0.35 + mouse.x * 2,
      ship.position.y + 10 + mouse.y * 1.5,
      ship.position.z + 48,
    );
    cam.position.lerp(desired, 0.08);
    this.lookTarget.set(
      ship.position.x * 0.2 + cameraCenter.x,
      ship.position.y + 2,
      ship.position.z - 60,
    );
    cam.lookAt(this.lookTarget);
  }

  private checkCollisions(): boolean {
    const ship = this.ship;
    if (!ship) return false;
    for (const cube of this.obstacles) {
      if (spheresCollide(ship, cube)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Immortal mode: any obstacle the ship is currently overlapping instantly
   * destroys with the normal debris burst (no game over).
   */
  private shatterCollidingObstacles() {
    const ship = this.ship;
    if (!ship) return;
    for (let i = this.obstacles.length - 1; i >= 0; i--) {
      if (spheresCollide(ship, this.obstacles[i])) {
        this.destroyObstacleAt(i);
      }
    }
  }

  private cleanupPlayObjects() {
    const { scene } = this.ctx;
    if (this.ship) {
      scene.remove(this.ship);
      disposeObject3D(this.ship);
      this.ship = null;
    }
    for (const cube of this.obstacles) {
      scene.remove(cube);
      cube.geometry.dispose();
      const mats = cube.material;
      if (Array.isArray(mats)) {
        mats.forEach((m) => m.dispose());
      } else {
        mats.dispose();
      }
    }
    this.obstacles = [];
    for (const missile of this.missiles) {
      scene.remove(missile);
      disposeObject3D(missile);
    }
    this.missiles = [];
    for (const bolt of this.rails) {
      scene.remove(bolt);
      disposeObject3D(bolt);
    }
    this.rails = [];
    for (const bolt of this.enemyRails) {
      scene.remove(bolt);
      disposeObject3D(bolt);
    }
    this.enemyRails = [];
    for (const missile of this.enemyMissiles) {
      scene.remove(missile);
      disposeObject3D(missile);
    }
    this.enemyMissiles = [];
    for (const bolt of this.allyRails) {
      scene.remove(bolt);
      disposeObject3D(bolt);
    }
    this.allyRails = [];
    for (const pod of this.chaffPods) {
      scene.remove(pod);
      disposeObject3D(pod);
    }
    this.chaffPods = [];
    this.chaffVolleyWavesLeft = 0;
    this.chaffVolleyTimer = 0;
    this.chaffVolleyIsFlares = false;
    this.regular = null;
    for (const enemy of this.enemies) {
      scene.remove(enemy.mesh);
      disposeObject3D(enemy.mesh);
    }
    if (this.enemies.length > 0) {
      this.enemies = [];
      this.callbacks.onEnemyChange?.(false);
    }
    if (this.teammates.length > 0) {
      for (const mate of this.teammates) {
        scene.remove(mate.mesh);
        disposeObject3D(mate.mesh);
      }
      this.teammates = [];
      this.callbacks.onTeammateChange?.(false);
    }
    this.dropEnemyLock({ requireRelease: false });
    this.lockReleaseRequired = false;
    this.immortal = false;
    this.paused = false;
    this.callbacks.onImmortalChange?.(false);
    for (const piece of this.debris) {
      scene.remove(piece.mesh);
      piece.mesh.geometry.dispose();
      (piece.mesh.material as THREE.MeshBasicMaterial).dispose();
    }
    this.debris = [];
    this.ammo = MISSILE_MAX;
    this.railAmmo = RAILGUN_MAX;
    this.chaffAmmo = CHAFF_MAX;
    this.weapon = 'missile';
    this.fireCooldown = 0;
    this.fireHeld = false;
    this.obstaclesEnabled = true;
    this.keys.clear();
  }
}
